// Per-subscriber detail. Phase 3a: overview + owner + locations + DIDs + SIP
// accounts, and lets org_admin edit identity, transition lifecycle status,
// assign/unassign locations and DIDs from the org pool, and soft-delete once
// server-side preconditions pass.
//
// All destructive/detach flows use the "type delete" confirm modal per
// feedback_delete_confirm_word.md — case-insensitive match on literal
// `delete`, never the object's name/id.
//
// Deferred from the portal shape: the /subscribers/:id/usage-timeseries
// 30-day chart lives here in the portal — parked until the org-tier metrics
// endpoint lands.

import {  useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate, useOutletContext, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient, useQueries } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDate, fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import RecordingsTab from '../components/subscribers/RecordingsTab.jsx';
import { canOperate, canAdmin } from '../lib/roles.js';
import LocationAssignPicker from '../components/locations/LocationAssignPicker.jsx';
import AccountUsersPanel from '../components/subscribers/AccountUsersPanel.jsx';
import { useRevealSecret, RevealSecretDialog } from '../components/shared/RevealSecretDialog.jsx';
import { LocationFormModal } from './LocationDetail.jsx';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import ActionMenu from '../components/shared/ActionMenu.jsx';
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import AddButton from '../components/shared/AddButton.jsx';
import FormModal from '../components/shared/FormModal.jsx';
import { usePbxNames } from '../hooks/usePbxNames.js';
import LineRunDialog from '../components/lines/LineRunDialog.jsx';
import UnattachedThings from '../components/subscribers/UnattachedThings.jsx';
import SetDevicePortDialog from '../components/lines/SetDevicePortDialog.jsx';
import { PendingChip } from '../components/lines/PendingChanges.jsx';
import useHashTab from '../hooks/useHashTab.js';
import { backTo } from '../hooks/useBackTo.js';
import { sharedList } from '../lib/liveQuery.js';

// Module scope on purpose: useHashTab memoises on these, and a fresh object literal every
// render would rebuild the callback and re-subscribe the popstate listener each time.
const SUBSCRIBER_TABS = ['overview', 'users', 'locations', 'lines', 'recordings', 'devices'];
const SUBSCRIBER_TAB_ALIASES = {
  dids: 'lines', routing: 'lines', extensions: 'lines',
  voicemail: 'lines', messaging: 'lines',
};

const TYPE_LABEL = { business: 'Business', residential: 'Residential' };
const STATUS_OPTIONS = ['active', 'suspended', 'cancelled', 'inactive'];

const stripPlus = (e) => String(e || '').replace(/^\+/, '');

function fmtDid(e164) {
  if (!e164) return '';
  const s = String(e164).replace(/[^0-9]/g, '');
  if (s.length === 11 && s[0] === '1') return `+1 (${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
  return String(e164);
}

// ------- Section wrapper -------
function Section({ title, subtitle, children, actions }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
        </div>
        {actions && <div className="flex flex-shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  );
}

// ------- Add a line -------
//
// Package first, then the number, then the address — in that order because each
// step constrains the next and because that is the order the sale happens in. The
// package decides which features the line may ever use, so picking it last would
// mean choosing features the package forbids and then being told no.
//
// What this DOES: records the sale in Verso — the line, its package, its number and
// its 911 address. What it does NOT do: touch a PBX, the carrier or GDMS. That is
// provisioning, it is a separate step, and it is currently frozen. Saying so on the
// last step is the difference between an operator knowing the phone will not ring
// yet and finding out from a customer.
function AddLineModal({ subscriberId, subscriberName, onClose, onCreated }) {
  const [step, setStep] = useState(1);
  const [packageId, setPackageId] = useState('');
  const [didId, setDidId] = useState('');
  // Two ways a line gets its number, and they are not variations of one form.
  //
  // Kevin, 2026-08-20: "a new subscriber can be added and a port request initiated instead of
  // assigning an existing DID from inventory." Christopher Sloan is the case — his number is at his
  // old carrier, so there is nothing in inventory to pick and the sale could not be recorded at all.
  const [source, setSource] = useState('inventory');
  const [port, setPort] = useState({
    number: '', losing_carrier: '', account_number: '', account_pin: '', authorized_name: '',
    requested_foc: '', notes: '',
  });
  const setPortField = (k) => (e) => setPort((p) => ({ ...p, [k]: e.target.value }));
  const portDigits = port.number.replace(/[^0-9]/g, '');
  const [locationId, setLocationId] = useState('');
  const [pbxAgentId, setPbxAgentId] = useState('');
  const [label, setLabel] = useState('');

  const tiersQ = useQuery({
    queryKey: ['service-tiers'],
    queryFn: () => api.get('/service-tiers'),
  });
  // Only numbers nobody holds. Offering an assigned number here is how you take a
  // working line off another customer.
  const invQ = useQuery({
    queryKey: ['dids', 'inventory'],
    queryFn: () => api.get('/dids', { unassigned: 1 }),
  });
  // THIS CUSTOMER'S addresses, not the org's.
  //
  // It listed every address in the org and filtered by package type, which let an
  // operator pick a street they had never assigned to this customer — and `createLine`
  // refuses that (`location_not_theirs`), because a line's address is its 911 answer and
  // borrowing another customer's would send an ambulance to the wrong house. A dropdown
  // that offers what the server will reject teaches nothing except that the form lies.
  //
  // Read from the subscriber payload, which already carries them, so there is one source
  // of "where is this customer" rather than a second query that can disagree.
  const subQ = useQuery({
    queryKey: ['subscriber', String(subscriberId)],
    queryFn: () => api.get(`/subscribers/${subscriberId}`),
  });
  const hostsQ = useQuery({
    queryKey: ['pbx-hosts'],
    queryFn: () => api.get('/pbx-hosts'),
  });

  const tiers = tiersQ.data?.tiers ?? [];
  const catalog = tiersQ.data?.catalog ?? {};
  const inventory = Array.isArray(invQ.data) ? invQ.data : (invQ.data?.dids ?? []);
  const allLocations = Array.isArray(subQ.data?.locations) ? subQ.data.locations : [];
  const chosenTier = tiers.find((t) => String(t.id) === String(packageId));

  // Only addresses that can legally carry this package. A business package at a
  // residential address (or the reverse) produces a number whose 911 registration
  // the carrier rejects: Commio requires the E911 registration type to match the
  // CNAM type (project_shared_locations).
  //
  // The package's type is a COLUMN, deliberately — not parsed from "Business
  // Standard". Deriving facts from name conventions has broken live things here
  // twice, and a name is a label people edit.
  //
  // A package with no type constrains nothing, so every address stays selectable
  // rather than none — an untyped package should be usable, not unplaceable.
  const wantedType = chosenTier?.type ?? null;
  const locations = wantedType
    ? allLocations.filter((l) => (l.type ?? l.locationType) === wantedType)
    : allLocations;
  const hiddenByType = allLocations.length - locations.length;

  const hosts = Array.isArray(hostsQ.data) ? hostsQ.data : (hostsQ.data?.hosts ?? []);

  const create = useMutation({
    mutationFn: () => api.post(`/subscribers/${subscriberId}/lines`, {
      package_id: Number(packageId),
      // One of the two, never both — a ported line has no number to assign yet.
      ...(source === 'port'
        ? {
          port: {
            number: port.number,
            losing_carrier: port.losing_carrier.trim() || null,
            account_number: port.account_number.trim() || null,
            account_pin: port.account_pin.trim() || null,
            authorized_name: port.authorized_name.trim() || null,
            requested_foc: port.requested_foc || null,
            notes: port.notes.trim() || null,
          },
        }
        : { did_id: Number(didId) }),
      location_id: Number(locationId),
      pbx_agent_id: pbxAgentId || null,
      label: label.trim() || null,
    }),
    onSuccess: (line) => onCreated?.(line),
  });

  // Host first. It is the authority now — the extension, the inbound route and the
  // carrier's delivery all follow the line's host — so it is the decision every
  // later one hangs off. Number last, because which numbers are sensible depends on
  // the host that will answer them.
  //
  // The host step does not block: a line can be sold before anyone decides which
  // PBX will serve it, which is the sold-not-provisioned state the model exists for.
  const canNext = step === 1
    || (step === 2 && packageId)
    || (step === 3 && locationId)
    // A port needs a number to port; everything else on that form is a rejection risk, not a
    // blocker, and holding the sale hostage to a PIN the customer has to phone in for is worse
    // than recording the port and chasing the PIN.
    || (step === 4 && (source === 'port' ? portDigits.length >= 10 : didId));
  const STEPS = ['PBX host', 'Package', 'Address', 'Number'];

  return (
    // The shared shell, like the other three adds. Its own `fixed inset-0` div had no
    // role="dialog" and no Escape handler, so this was the one add on the page that was not
    // a dialog to a screen reader and could only be dismissed by finding Cancel.
    <FormModal
      title="Add a line"
      subtitle={subscriberName ? `for ${subscriberName}` : null}
      onClose={onClose}
    >
      <div>
        <ol className="flex gap-2 text-xs" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li
              key={s}
              aria-current={step === i + 1 ? 'step' : undefined}
              className={`rounded-full px-2.5 py-1 font-medium ${
                step === i + 1 ? 'bg-blue-100 text-blue-700'
                  : step > i + 1 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
              }`}
            >
              {i + 1}. {s}
            </li>
          ))}
        </ol>

        <div className="mt-5 min-h-[9rem]">
          {step === 2 && (
            <>
              <label className="block text-sm font-medium text-gray-700" htmlFor="line-package">
                Package
              </label>
              <p className="mb-2 mt-0.5 text-xs text-gray-500">
                A line <em>is</em> a package. It decides which features this line may use.
              </p>
              <select
                id="line-package"
                value={packageId}
                onChange={(e) => {
                  setPackageId(e.target.value);
                  // Clear any address chosen under the previous package. Business and
                  // residential addresses are not interchangeable, so going Back and
                  // switching package would otherwise leave a selection the new
                  // package cannot legally use — and the operator would never see it
                  // change.
                  setLocationId('');
                }}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Choose a package…</option>
                {tiers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              {tiers.length === 0 && !tiersQ.isLoading && (
                <p className="mt-2 text-sm text-amber-700">
                  No packages defined for this org yet — an admin creates them in the admin console.
                </p>
              )}
              {chosenTier && (
                <ul className="mt-3 space-y-0.5 text-xs text-gray-600">
                  {Object.entries(chosenTier.features || {})
                    .filter(([, v]) => v && v !== 'none')
                    .map(([k, v]) => (
                      <li key={k}>
                        ✓ {catalog[k]?.label || k}
                        {typeof v === 'string' && v !== 'true' && <span className="text-gray-400"> · {v}</span>}
                      </li>
                    ))}
                </ul>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <div className="mb-3 flex gap-2" role="radiogroup" aria-label="Where the number comes from">
                {[
                  ['inventory', 'From inventory', 'A number we already hold'],
                  ['port', 'Port one in', 'The customer keeps their number'],
                ].map(([key, title, sub]) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={source === key}
                    onClick={() => setSource(key)}
                    className={`flex-1 rounded-md border px-3 py-2 text-left ${
                      source === key ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className="block text-sm font-medium text-gray-900">{title}</span>
                    <span className="block text-xs text-gray-500">{sub}</span>
                  </button>
                ))}
              </div>

              {source === 'inventory' ? (
                <>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="line-did">
                    Number
                  </label>
                  <p className="mb-2 mt-0.5 text-xs text-gray-500">
                    From inventory — numbers nobody holds yet.
                  </p>
                  <select
                    id="line-did"
                    value={didId}
                    onChange={(e) => setDidId(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">Choose a number…</option>
                    {inventory.map((d) => (
                      <option key={d.id} value={d.id}>{fmtDid(d.e164)}</option>
                    ))}
                  </select>
                  {inventory.length === 0 && !invQ.isLoading && (
                    <p className="mt-2 text-sm text-amber-700">
                      Nothing in inventory. Import numbers from the carrier, or return one from
                      another line first.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <label className="block text-sm font-medium text-gray-700" htmlFor="port-number">
                    Number being ported in
                  </label>
                  <input
                    id="port-number"
                    value={port.number}
                    onChange={setPortField('number')}
                    placeholder="(720) 893-2452"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />

                  {/* WHAT REJECTIONS ARE ACTUALLY MADE OF. The losing carrier checks these against
                      their own record, and a mismatch comes back days later as a refusal with no
                      hint of which field was wrong. Collecting them while the customer is on the
                      phone is the entire difference between a two-week port and a six-week one. */}
                  <p className="mb-2 mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                    What the old carrier checks
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      aria-label="Current carrier"
                      value={port.losing_carrier}
                      onChange={setPortField('losing_carrier')}
                      placeholder="Current carrier"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <input
                      aria-label="Account number with that carrier"
                      value={port.account_number}
                      onChange={setPortField('account_number')}
                      placeholder="Account number"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <input
                      aria-label="Account PIN"
                      value={port.account_pin}
                      onChange={setPortField('account_pin')}
                      placeholder="Account PIN"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                    <input
                      aria-label="Name on the account"
                      value={port.authorized_name}
                      onChange={setPortField('authorized_name')}
                      placeholder="Name on the account"
                      className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                    />
                  </div>
                  <label className="mt-3 block text-sm font-medium text-gray-700" htmlFor="port-foc">
                    Requested port date <span className="font-normal text-gray-400">(optional)</span>
                  </label>
                  <input
                    id="port-foc"
                    type="date"
                    value={port.requested_foc}
                    onChange={setPortField('requested_foc')}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  />
                </>
              )}
              <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="line-label">
                Label <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="line-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Front desk"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
              {/* Said on the step with the button, not after it. An operator who
                  thinks this provisions the line will tell a customer their phone
                  is live. */}
              {/* Said on the step with the button, and said DIFFERENTLY for a port, because the
                  next action is not the same one. An operator who thinks this filed the port with
                  the carrier will wait for a date that is never coming. */}
              <p className="mt-4 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700">
                {source === 'port'
                  ? 'This records the sale and the port request in Verso. It does NOT file the port '
                    + 'with the carrier — raise the order in the Commio portal, then paste its port '
                    + 'ID onto this line. Verso watches the carrier and marks the number as arrived.'
                  : 'This records the sale in Verso only. Nothing is sent to the PBX, the carrier or '
                    + 'GDMS, so the number will not ring yet — provisioning is a separate step.'}
              </p>
            </>
          )}

          {step === 3 && (
            <>
              <label className="block text-sm font-medium text-gray-700" htmlFor="line-location">
                Service address
              </label>
              <p className="mb-2 mt-0.5 text-xs text-gray-500">
                This is the <strong>911 address</strong> for this number. One line, one address.
                {wantedType && (
                  <>
                    {' '}Showing <strong>{wantedType}</strong> addresses only — {chosenTier.name} is{' '}
                    {wantedType} service, and the carrier requires the 911 registration type to
                    match.
                  </>
                )}
              </p>
              <select
                id="line-location"
                value={locationId}
                onChange={(e) => setLocationId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Choose an address…</option>
                {locations.map((l) => (
                  <option key={l.id} value={l.id}>{l.label || l.address_line1 || l.addressLine1}</option>
                ))}
              </select>
              {locations.length === 0 && !subQ.isLoading && (
                <p className="mt-2 text-sm text-amber-700">
                  {/* Two different problems, and telling them apart is the whole
                      point: "add an address" and "add a BUSINESS address" send an
                      operator to different places. */}
                  {hiddenByType > 0
                    ? `No ${wantedType} addresses on file — the ${hiddenByType} address${hiddenByType === 1 ? '' : 'es'} this customer has cannot carry ${wantedType} service. Add one on the Locations tab, or pick a different package.`
                    : 'No addresses on file. Add one on the Locations tab first — a line cannot be sold without a 911 address.'}
                </p>
              )}
              {locations.length > 0 && hiddenByType > 0 && (
                <p className="mt-1.5 text-xs text-gray-500">
                  {hiddenByType} other address{hiddenByType === 1 ? '' : 'es'} hidden — wrong type for
                  this package.
                </p>
              )}
            </>
          )}

          {step === 1 && (
            <>
              <label className="block text-sm font-medium text-gray-700" htmlFor="line-pbx">
                PBX host <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <p className="mb-2 mt-0.5 text-xs text-gray-500">
                Which PBX will serve this number. Everything PBX-side derives from this —
                the extension, the inbound route and the carrier&rsquo;s delivery all follow
                the line&rsquo;s host, which is why it belongs on the line and not on the
                extension.
              </p>
              <select
                id="line-pbx"
                value={pbxAgentId}
                onChange={(e) => setPbxAgentId(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">Decide later</option>
                {hosts.map((h) => (
                  <option key={h.agent_id || h.agentId} value={h.agent_id || h.agentId}>
                    {h.name || h.agent_id || h.agentId}
                  </option>
                ))}
              </select>
              {/* Leaving it unset is a real choice, not a gap — say so rather than
                  letting an operator think they missed a required field. */}
              <p className="mt-2 text-xs text-gray-500">
                Leave as “Decide later” to record the sale now and choose the host when the
                line is provisioned.
              </p>
            </>
          )}
        </div>

        {create.isError && (
          <p className="mt-3 text-sm text-red-600">
            {create.error?.body?.error || create.error?.message || 'Could not add the line'}
          </p>
        )}

        <div className="mt-5 flex justify-between">
          <button
            type="button"
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          {step < 4 ? (
            <button
              type="button"
              disabled={!canNext}
              onClick={() => setStep(step + 1)}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              disabled={!canNext || create.isPending}
              onClick={() => create.mutate()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
            >
              {create.isPending ? 'Adding…' : 'Add line'}
            </button>
          )}
        </div>
      </div>
    </FormModal>
  );
}

// ------- Set or change a line's PBX host -------
//
// The gap this closes: the wizard let an operator answer "Decide later", and then
// there was no way to answer it afterwards. A field you can leave blank and never
// fill is worse than a required one.
//
// Verso-only, and it refuses on the server for a line a PBX already serves — moving
// a live line between hosts is a make-before-break saga, not a field change. The
// copy says which of the two this is before the button rather than after the 409.
function SetLineHostModal({ line, onSave, onClose, saving, error }) {
  const [agentId, setAgentId] = useState(line.pbxAgentId || '');
  const hostsQ = useQuery({ queryKey: ['pbx-hosts'], queryFn: () => api.get('/pbx-hosts') });
  const hosts = Array.isArray(hostsQ.data) ? hostsQ.data : (hostsQ.data?.hosts ?? []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          {line.pbxAgentId ? 'Change PBX host' : 'Assign a PBX host'}
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {line.number ? fmtDid(line.number) : `line ${line.id}`}
        </p>

        <label className="mt-4 block text-sm font-medium text-gray-700" htmlFor="set-line-pbx">
          PBX host
        </label>
        <p className="mb-2 mt-0.5 text-xs text-gray-500">
          Everything PBX-side follows this — the extension, the inbound route and the
          carrier&rsquo;s delivery all derive from the line&rsquo;s host.
        </p>
        <select
          id="set-line-pbx"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="">Not set</option>
          {hosts.map((h) => (
            <option key={h.agent_id || h.agentId} value={h.agent_id || h.agentId}>
              {h.name || h.agent_id || h.agentId}
            </option>
          ))}
        </select>

        {line.provisioned ? (
          // Named before the button. The server refuses this, and an operator who
          // only finds that out from an error assumes the feature is broken rather
          // than that they are asking for the wrong tool.
          <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
            This line is already provisioned on {line.pbxName || 'a PBX'} as extension{' '}
            {line.extension}. Moving a live line to another host is a migration, not a
            field change — use <strong>Migrate to another PBX</strong> so the extension,
            the route and the carrier move together.
          </p>
        ) : (
          <p className="mt-4 rounded-md bg-slate-100 px-3 py-2 text-xs text-slate-700">
            Records the host in Verso. Nothing is built on the PBX until this line is
            provisioned.
          </p>
        )}

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || String(agentId) === String(line.pbxAgentId || '')}
            onClick={() => onSave(agentId)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save host'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ------- One line's own configuration -------
//
// THE INLINE LINE PANEL IS GONE.
//
// Kevin, 2026-08-19: "the panel is busy and hard to read. it doesn't serve a great purpose. its too
// busy without much to break up the information."
//
// It was three columns of read-only fields — package, number, caller-ID name, 911 address, host,
// extension, handset name, device, carrier route, and a feature list — every one of which the line's
// own page already shows, with more room and more truth. Keeping it meant keeping ten facts correct
// in a second place, and it was not managing:
//
//   THREE OF FOUR FEATURE DOTS WERE HARDCODED. `caller_id`, `call_forwarding` and `sms` rendered a
//   grey ○ with no data behind them, so caller ID read "off" on Carole Carroll's line while the
//   carrier presented "Carole Carroll" on every outbound call, and SMS read off by luck rather than
//   by consulting the gate that decides it.
//
//   THE FOOTNOTE CLAIMED WRITES WERE BLOCKED. "Switching a feature on needs a write to the PBX or the
//   carrier, which the refactor freeze blocks" — the freeze has been off since `EXTERNAL_WRITES =
//   "enabled"` landed, and builds had been executing for real all day.
//
//   THE CARRIER ROUTE WAS A FOSSIL. `route_summary` is denormalised and was never refreshed, so it
//   named the profile a number had LEFT — "LyonsCommPBX" over a number now on 45509.
//
// The row keeps what a table is for: number, host, address, package, status, sync. Everything else is
// one click away on a page built to hold it, and "Configure line" now opens the settings sheet
// instead of unfolding a summary that could not be trusted.

// ------- Lines table -------
//
// One row per thing the customer buys. The columns are deliberately in the order
// the questions get asked: which number, what did they buy, which PBX serves it,
// how is it realised, where is it plugged in, is it working.
//
// `pbx` is second because it is the host authority — everything PBX-side derives
// from the line's assignment, and putting it next to the number is what makes a
// mismatch visible rather than something you go looking for.
function LinesTable({
  // `tiers` and `catalog` left with the panel: the table shows the package NAME, which the line row
  // already carries, and nothing here needs the feature catalogue any more.
  lines, loading, error, onAdd, canAdd, navigate,
  onSetHost, onSetStatus, onDelete, onBuild, onTakeDown, onSetPort,
  // Where the back arrow on a line/device page should return to — this tab, not the
  // subscriber's Overview and not the global device list. See hooks/useBackTo.js.
  backHere,
}) {
  // One open at a time. Everything about a line is configured inside the line, so
  // the expanded panel is the workspace — several open at once turns the tab back
  // into the wall of sections this replaced.

  if (loading) return <LoadingSpinner />;
  if (error) {
    return (
      <p className="text-sm text-red-700">
        Could not load lines: {error.body?.error || error.message}
      </p>
    );
  }
  // Nothing configured yet, so offer the one thing there is to do. Everything else
  // on this tab lives inside a line, and with no lines there is nothing to show —
  // an empty table with eight column headings would just be furniture.
  if (!lines.length) {
    return (
      <div className="py-8 text-center">
        <p className="text-sm text-gray-600">
          No lines yet. A line is a number plus the package sold with it — everything else
          is configured inside one.
        </p>
        {canAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="mt-4 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Add a line
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead>
          {/* Four things, not eight. Which number, which PBX serves it, where it is,
              and what was sold — that is what an operator scans a list of lines for.
              Extension, device, port and voicemail are how it is built rather than
              what it is, so they moved into the row's own panel where there is room
              to label them. */}
          <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-2"><span className="sr-only">Configure</span></th>
            <th className="py-2 pr-4">Number</th>
            <th className="py-2 pr-4">PBX</th>
            <th className="py-2 pr-4">Service address</th>
            <th className="py-2 pr-4">Package</th>
            <th className="py-2 pr-4">Status</th>
            {/* Sold-vs-built and unpushed work, per line. This is what the retired
                customer-wide "Push N pending changes" button could not tell you: which
                one. */}
            <th className="py-2 pr-4">In sync</th>
            <th className="py-2 pr-2"><span className="sr-only">Actions</span></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {lines.map((l) => (
            <Fragment key={l.id}>
            <tr className="align-top hover:bg-gray-50">
              {/* Number carries the label and the extension underneath it — same
                  cell, because "which line" and "what is it called" are one thought,
                  and it keeps the row to one line of real columns. */}
              <td className="py-2.5 pr-4">
                {/* To the LINE, not the number's carrier record. This table is the index
                    of what the customer buys, and the line is the object every action
                    now works on; the carrier record is a click further in, from there. */}
                <Link to={`/lines/${l.id}`} state={backHere} className="font-medium text-blue-600 hover:underline">
                  {l.number ? fmtDid(l.number) : <span className="text-gray-400">no number</span>}
                </Link>
                <div className="text-xs text-gray-500">
                  {l.label && <span>{l.label}</span>}
                  {l.label && l.extension && <span className="text-gray-300"> · </span>}
                  {l.extension
                    ? <span>ext {l.extension}</span>
                    /* Sold but not built is a normal state, and the reason `lines` is
                       its own table — say so rather than showing nothing. */
                    : <span className="text-gray-400">not provisioned</span>}
                </div>
              </td>
              <td className="py-2.5 pr-4">
                {l.pbxName || <span className="text-amber-700" title="No host chosen for this line yet">not set</span>}
              </td>
              <td className="py-2.5 pr-4">
                {l.locationLabel || l.locationAddress
                  // A line with no address has no 911 answer, so it is called out
                  // rather than left as an em dash.
                  || <span className="text-amber-700" title="No 911 address on this line">not set</span>}
              </td>
              {/* Package and business-vs-residential in one cell: the type IS a
                  property of the package, so splitting them would spend a column
                  restating it. */}
              <td className="py-2.5 pr-4">
                {l.packageName || <span className="text-amber-700">none</span>}
                {l.locationType && (
                  <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                    {l.locationType}
                  </span>
                )}
              </td>
              <td className="py-2.5 pr-4">
                <StatusBadge
                  status={l.status === 'active' ? 'active' : l.status === 'suspended' ? 'warning' : 'inactive'}
                  label={l.status || 'unknown'}
                />
              </td>
              <td className="py-2.5 pr-4">
                <Link to={`/lines/${l.id}`} state={backHere} className="hover:underline">
                  <PendingChip pending={l.pending} />
                </Link>
              </td>
              <td className="py-2.5 pr-2 text-right">
                <ActionMenu
                  title={`Actions for ${l.number || l.label || `line ${l.id}`}`}
                  items={[
                    { label: 'Open this line', onClick: () => navigate(`/lines/${l.id}`) },
                    // NO "Configure line" HERE AT ALL. Kevin, 2026-08-19: "I question if it is
                    // really needed or not." It is not: "Open this line" lands on the page whose
                    // gear is the one editor for these fields, and a second door to the same sheet is
                    // the shape this codebase keeps having to close — two surfaces that drift, or two
                    // places to look for one control. The menu is navigation and verbs; editing has
                    // an address.

                    ...(l.number ? [{ label: 'Open number', onClick: () => navigate(`/dids/${encodeURIComponent(l.number)}`) }] : []),
                    ...(l.sipAccountId ? [{ label: 'Open extension', onClick: () => navigate(`/extensions/${l.sipAccountId}`) }] : []),
                    ...(l.deviceId ? [{ label: 'Open device', onClick: () => navigate(`/devices/${l.deviceMac || l.deviceId}`) }] : []),
                    ...(l.locationId ? [{ label: 'Open service address', onClick: () => navigate(`/locations/${l.locationId}`) }] : []),
                    ...(onBuild || onSetPort ? [
                      { divider: true },
                      // The action this tab exists for, and until now the one thing an
                      // operator could NOT do here: everything a line needs on its PBX,
                      // its handset and the carrier. Shows the plan before it runs.
                      ...(onBuild ? [{
                        label: l.provisioned ? 'Re-run build' : 'Build this line',
                        onClick: () => onBuild(l),
                      }] : []),
                      ...(onSetPort ? [{
                        // "device", not "handset": half of these are ATAs, door phones and
                        // DECT bases, and calling an ATA a handset is simply wrong on screen.
                        label: l.deviceMac ? 'Change the device or port' : 'Assign a device',
                        onClick: () => onSetPort(l),
                      }] : []),
                      // Only offered once a PBX is actually serving it. Offering a
                      // teardown for a line that was never built is an invitation to a
                      // confusing no-op.
                      ...(onTakeDown && l.provisioned ? [{
                        label: 'Take down', danger: true, onClick: () => onTakeDown(l),
                      }] : []),
                    ] : []),
                    ...(onSetHost ? [
                      { divider: true },
                      // The gap after the wizard: there was no way to set or change a
                      // line's host once it existed, which made "Decide later" a
                      // dead end.
                      { label: l.pbxAgentId ? 'Change PBX host' : 'Assign a PBX host',
                        onClick: () => onSetHost(l) },
                    ] : []),
                    ...(onSetStatus ? [
                      { divider: true },
                      l.status === 'suspended'
                        ? { label: 'Reactivate', onClick: () => onSetStatus(l, 'active') }
                        : { label: 'Suspend', onClick: () => onSetStatus(l, 'suspended') },
                      { label: 'Mark inactive',
                        disabled: l.status === 'inactive',
                        title: l.status === 'inactive' ? 'Already inactive' : undefined,
                        onClick: () => onSetStatus(l, 'inactive') },
                    ] : []),
                    ...(onDelete ? [
                      { divider: true },
                      { label: 'Delete line', danger: true, onClick: () => onDelete(l) },
                    ] : []),
                  ]}
                />
              </td>
            </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ------- Field row -------
function Field({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value ?? <span className="text-gray-300">—</span>}</dd>
    </div>
  );
}

function TypePill({ type }) {
  const label = TYPE_LABEL[type] || type || '—';
  const cls = type === 'business'
    ? 'bg-blue-100 text-blue-700'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>
      {label}
    </span>
  );
}

// Calix router health.
//
// This used to fetch only on a click, because "their API forbids mass/non-interactive polling" —
// a real constraint, met by making the operator ask. The worker now caches per router for five
// minutes (routes/calix.js), so every surface that wants the router's state costs one Calix call
// between them however often they are opened, and Kevin asked for it drawn on render rather than
// behind a button (2026-08-21).
//
// Refresh still goes to the router: it calls with `?refresh=1` and writes the answer into the same
// cache entry, so the Devices tab and the line's call path move with it.
function CalixRouterCard({ subscriberId, fsan }) {
  const qc = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const q = useQuery({
    queryKey: ['subscriber', subscriberId, 'calix-health'],
    queryFn: () => api.get(`/subscribers/${subscriberId}/calix-health`),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const refresh = async () => {
    setRefreshing(true);
    try {
      const fresh = await api.get(`/subscribers/${subscriberId}/calix-health?refresh=1`);
      qc.setQueryData(['subscriber', subscriberId, 'calix-health'], fresh);
    } catch { /* the card keeps showing the last good answer */ }
    setRefreshing(false);
  };
  const busy = q.isFetching || refreshing;
  const d = q.data;
  const h = d?.health;

  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="rounded bg-indigo-100 px-1.5 py-0.5 text-xs font-semibold text-indigo-700">Calix</span>
          <span className="text-sm font-medium text-gray-800">Router</span>
          <span className="font-mono text-xs text-gray-500">{fsan}</span>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {busy ? 'Checking…' : 'Refresh'}
        </button>
      </div>

      {busy && !h && (
        <p className="mt-2 text-xs text-gray-500">
          Asking Calix Cloud about this router…
        </p>
      )}

      {q.isError && (
        <p className="mt-2 text-sm text-red-600">Lookup failed. Try again shortly.</p>
      )}

      {d && !d.configured && d.reason === 'integration_not_configured' && (
        <p className="mt-2 text-sm text-amber-600">
          Calix integration isn’t configured. Add credentials in Settings → Integrations.
        </p>
      )}

      {d?.error && (
        <p className="mt-2 text-sm text-red-600">
          {d.rate_limited ? 'Calix is rate-limiting requests — try again shortly.' : d.error}
        </p>
      )}

      {h && (
        <div className="mt-2">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
              h.online ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${h.online ? 'bg-green-500' : 'bg-red-500'}`} />
              {h.online ? 'Online' : 'Offline'}
            </span>
            {h.model && <span className="text-xs text-gray-600">{h.model}</span>}
            {h.firmware && <span className="text-xs text-gray-400">fw {h.firmware}</span>}
          </div>
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
            {h.lastInform && <div><dt className="text-gray-400">Last check-in</dt><dd className="text-gray-700">{h.lastInform}</dd></div>}
            {h.uptime != null && <div><dt className="text-gray-400">Uptime</dt><dd className="text-gray-700">{h.uptime}s</dd></div>}
            {h.wanIp && <div><dt className="text-gray-400">WAN IP</dt><dd className="font-mono text-gray-700">{h.wanIp}</dd></div>}
            {h.macAddress && <div><dt className="text-gray-400">MAC</dt><dd className="font-mono text-gray-700">{h.macAddress}</dd></div>}
          </dl>
          {Array.isArray(h.issues) && h.issues.length > 0 && (
            <ul className="mt-2 space-y-0.5">
              {h.issues.map((i, idx) => (
                <li key={idx} className="text-xs text-amber-700">
                  ⚠ {i.description || i.code || i.type}
                </li>
              ))}
            </ul>
          )}
          <p className="mt-2 text-[11px] text-gray-400">
            Router health only — the ATA’s voice-line registration is shown under Routing.
          </p>
        </div>
      )}
    </div>
  );
}

function statusVariant(status) {
  switch (status) {
    case 'active':    return 'active';
    case 'suspended': return 'warning';
    case 'cancelled': return 'critical';
    case 'inactive':  return 'inactive';
    default:          return 'unknown';
  }
}

// ------- Edit / Create identity modal -------
export function SubscriberFormModal({ initial, title, submitLabel, onSubmit, onCancel, isPending, error, portalAllowed = true }) {
  const [displayName, setDisplayName] = useState(initial?.name ?? initial?.display_name ?? '');
  const [type, setType]               = useState(initial?.type || 'business');
  const [phone, setPhone]             = useState(initial?.phone ?? '');
  const [email, setEmail]             = useState(initial?.email ?? '');
  const [notes, setNotes]             = useState(initial?.notes ?? '');
  const [billingAccount, setBillingAccount] = useState(initial?.billingAccount ?? initial?.billing_account ?? '');
  const initialPortalEmail = initial?.portalEmail ?? initial?.portal_email ?? '';
  const [portalEmail, setPortalEmail] = useState(initialPortalEmail);

  const submit = (e) => {
    e.preventDefault();
    if (!displayName.trim()) return;
    const patch = {
      display_name: displayName.trim(),
      type,
      phone: phone.trim() || null,
      email: email.trim() || null,
      notes: notes.trim() || null,
      billing_account: billingAccount.trim() || null,
    };
    // Only send portal_email if the operator changed it — sending it
    // unchanged would still bump the session_epoch and log everyone out
    // for no reason.
    const nextPortalEmail = portalEmail.trim().toLowerCase() || null;
    if (nextPortalEmail !== (initialPortalEmail || '').trim().toLowerCase() || null) {
      // Send even when null-becomes-null so 'portal_email' in body === true
      // and the server takes the (undefined-vs-null) branch. Cheaper than
      // a second-level flag.
      patch.portal_email = nextPortalEmail;
    }
    onSubmit(patch);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Display name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoFocus
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Acme Corp"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="business">Business</option>
              <option value="residential">Residential</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Phone</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Billing account #</label>
            <input
              type="text"
              value={billingAccount}
              onChange={(e) => setBillingAccount(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Account number in your billing system"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700">
              Portal login email
              <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-normal text-blue-700">
                Self-service portal
              </span>
            </label>
            <input
              type="email"
              value={portalEmail}
              onChange={(e) => setPortalEmail(e.target.value)}
              autoComplete="off"
              placeholder="billing@customer.example"
              disabled={!portalAllowed}
              title={portalAllowed ? undefined : 'Portal access is not included in this subscriber’s service tier'}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            />
            {portalAllowed ? (
              <p className="mt-1 text-xs text-gray-500">
                This email can sign into the read-only subscriber portal via
                magic link. Clear the field to revoke portal access —
                changing or clearing signs the customer out immediately.
              </p>
            ) : (
              <p className="mt-1 text-xs text-amber-600">
                Portal access is not included in this subscriber’s service tier.
                Assign a tier that includes portal access to enable a login.
              </p>
            )}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !displayName.trim()}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {isPending ? 'Saving...' : submitLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ------- Assign-from-pool picker (locations / DIDs) -------
// StatusChangeDialog — what a lifecycle transition is about to do, BEFORE it does it.
//
// Kevin, 2026-08-14, after cancelling Main Stage Brewing: "does cancelation require operator
// confirmation?" It did not. The kebab item called `changeStatus.mutate(s)` directly, so one
// click deleted two extensions from FreePBX on PBX-01, deleted two GDMS accounts and
// rewrote carrier routing — for a live customer, with no preview and no confirmation.
//
// The asymmetry was the tell: two lines below it in the same menu, "Delete" — a soft delete
// of one Verso row — made you type the word. The safest action was guarded and the one that
// reached three external systems was not. "yes, this is service impacting, so the operator
// should be in the loop confirming that is what they intended to do."
//
// So: fetch the same plan the run will execute, show it grouped by system, let the operator
// drop a system, and confirm. Cancelling types the word, because it deletes things on live
// hosts; suspending and reactivating do not, because they are reversible — the rule is the
// consequence, not the menu.
function StatusChangeDialog({ subscriberId, subscriberName, to, from, onCancel, onConfirm, isPending, error }) {
  const [systems, setSystems] = useState(['pbx', 'gdms', 'commio']);
  const planQ = useQuery({
    queryKey: ['subscriber', String(subscriberId), 'status-plan', to],
    queryFn: () => api.get(`/subscribers/${subscriberId}/status/plan`, { to }),
  });

  const plan = planQ.data;
  const groups = {};
  for (const step of (plan?.steps || [])) (groups[step.system] ||= []).push(step);
  // Verso's own bookkeeping is not a system anybody opts out of — it is the record that the
  // decision was made at all.
  const external = ['pbx', 'gdms', 'commio'].filter((k) => groups[k]?.length);

  const toggle = (k) => setSystems((cur) => (cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]));

  const WORD = { cancelled: 'cancel service', suspended: 'suspend service', active: 'restore service', inactive: 'close the account' };
  const title = `${WORD[to] ? WORD[to][0].toUpperCase() + WORD[to].slice(1) : `Change status to ${to}`} for ${subscriberName}?`;

  return (
    <ConfirmDeleteDialog
      title={title}
      body={
        to === 'cancelled'
          ? 'This takes their phones out of service. Extensions are deleted from the PBX, the '
            + 'GDMS accounts go with them, and their numbers return to inventory.'
          : to === 'suspended'
            ? 'This stops their calls. Nothing is deleted, and reactivating puts it all back.'
            : `This moves ${subscriberName} from ${from} to ${to}.`
      }
      // Typing the word for the one that deletes things on a live host. Suspension is a
      // switch; cancellation is a demolition.
      destructive={to === 'cancelled'}
      confirmLabel={to === 'cancelled' ? 'Cancel their service' : `Change to ${to}`}
      onConfirm={() => onConfirm(systems)}
      onCancel={onCancel}
      isPending={isPending}
      error={error}
      extra={(
        <div className="mt-3">
          {planQ.isLoading && <p className="text-sm text-gray-500">Working out what this will do…</p>}
          {planQ.isError && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              The plan could not be loaded, so this dialog cannot say what the change will do.
              Cancel and try again rather than guessing.
            </p>
          )}
          {plan && !plan.steps?.length && (
            <p className="text-sm text-gray-600">Nothing outside Verso changes.</p>
          )}
          {plan && plan.steps?.length > 0 && (
            <>
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                What this will do
              </p>
              <div className="mt-1.5 max-h-56 overflow-y-auto rounded-md border border-gray-200">
                {Object.entries(groups).map(([sys, steps]) => (
                  <div key={sys} className="border-b border-gray-100 px-3 py-2 last:border-b-0">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{sys}</p>
                    <ul className="mt-1 space-y-0.5">
                      {steps.map((st) => (
                        <li key={st.id} className="text-xs text-gray-700">· {st.description}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {external.length > 0 && (
                <>
                  <p className="mt-3 text-xs font-medium uppercase tracking-wide text-gray-500">
                    Systems to write to
                  </p>
                  <div className="mt-1 flex flex-wrap gap-3">
                    {external.map((k) => (
                      <label key={k} className="flex items-center gap-1.5 text-xs text-gray-700">
                        <input type="checkbox" checked={systems.includes(k)} onChange={() => toggle(k)} />
                        {{ pbx: 'PBX', gdms: 'Device (GDMS)', commio: 'Carrier (Commio)' }[k]}
                      </label>
                    ))}
                  </div>
                  {/* UNCHECKING IS NOT A DRY RUN. A skipped step leaves the customer
                      half-changed across systems, which is harder to see than either end
                      state — so say it here rather than in the run report afterwards. */}
                  {external.some((k) => !systems.includes(k)) && (
                    <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
                      Leaving a system out applies the change everywhere else. That is a
                      customer who is cancelled in one place and live in another.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    />
  );
}

// StatusFanOutPanel — shown after a lifecycle transition. Renders the plan
// the server returned so the operator knows what still needs manual action
// in external systems (Verso-side steps happened inline; PBX/GDMS/Commio
// steps are TODO until the write-adapters land). Groups by system, marks
// each step with its autoExecutable flag.
function StatusFanOutPanel({ plan, onDismiss }) {
  // Real outcomes, keyed by step id. A step with no execution record was never
  // attempted — which is itself worth showing, not hiding behind a tick.
  const outcome = {};
  for (const r of (plan.execution?.steps || [])) outcome[r.id] = r;

  // What the systems said when the server went back and read them. A step is
  // "committed" because a write returned 200; it is CONFIRMED only if someone
  // then looked. Those are different claims and the panel has to keep them apart
  // — conflating them is the whole reason suspension read as working for months.
  const verification = plan.execution?.verification || null;
  const checkFor = {};
  // Findings that belong to no single step — chiefly "this host was never
  // reloaded", which is about the plan rather than any one write. Without their
  // own line they'd contribute to the headline count and then be invisible.
  const looseChecks = [];
  for (const c of (verification?.checks || [])) {
    if (c.stepId) checkFor[c.stepId] = c; else looseChecks.push(c);
  }

  const groups = {};
  for (const step of plan.steps) {
    (groups[step.system] ||= []).push(step);
  }

  // Count what HAPPENED, not what was planned.
  const done    = plan.steps.filter((s) => ['ok', 'already-applied'].includes(outcome[s.id]?.status)).length;
  const skipped = plan.steps.filter((s) => outcome[s.id]?.status === 'skipped').length;
  const failed  = plan.steps.filter((s) => outcome[s.id]?.status === 'error' || outcome[s.id]?.status === 'failed').length;
  const manual  = plan.steps.filter((s) => !s.autoExecutable).length;
  const frozen  = plan.steps.filter((s) => outcome[s.id]?.status === 'frozen').length;

  // How each outcome reads. `skipped` is the one that matters: it means the step
  // did not run, and it used to render identically to success.
  const MARK = {
    ok:                { icon: '✓', cls: 'bg-green-100 text-green-700',  title: 'Committed' },
    'already-applied': { icon: '✓', cls: 'bg-green-100 text-green-700',  title: 'Already in that state' },
    skipped:           { icon: '!', cls: 'bg-amber-200 text-amber-900',  title: 'NOT run — this system was not included' },
    // Its own mark, deliberately not the skipped one. "We chose not to touch a
    // live phone system" and "there was nothing to do here" are different facts,
    // and an operator reading a run needs to tell them apart.
    frozen:            { icon: '⊘', cls: 'bg-slate-200 text-slate-800',  title: 'BLOCKED by the refactor freeze — no write was sent' },
    error:             { icon: '×', cls: 'bg-red-100 text-red-700',      title: 'Failed' },
    failed:            { icon: '×', cls: 'bg-red-100 text-red-700',      title: 'Failed' },
  };
  const systemLabels = {
    verso:  { label: 'Verso',           cls: 'bg-blue-100 text-blue-700'   },
    gdms:   { label: 'GDMS',            cls: 'bg-amber-100 text-amber-800' },
    commio: { label: 'Commio',          cls: 'bg-purple-100 text-purple-700' },
    pbx:    { label: 'PBX (on-prem)',   cls: 'bg-emerald-100 text-emerald-800' },
  };
  const order = ['verso', 'pbx', 'gdms', 'commio'];
  const orderedSystems = order.filter((s) => groups[s]);

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-amber-900">
            Deprovisioning plan — {plan.from} → {plan.to}
          </h2>
          <p className="mt-1 text-sm text-amber-800">
            {done} step{done === 1 ? '' : 's'} committed
            {skipped > 0 && <>, <strong>{skipped} not run</strong></>}
            {frozen > 0 && <>, <strong>{frozen} blocked</strong></>}
            {failed > 0 && <>, <strong>{failed} failed</strong></>}
            {manual > 0 && <>, {manual} need{manual === 1 ? 's' : ''} manual action</>}.
          </p>
          {frozen > 0 && (
            <p className="mt-1 text-sm font-medium text-slate-800">
              The steps marked <span className="font-bold">⊘</span> were blocked by the
              line-model refactor freeze. No write was sent to the phone system, the carrier or
              GDMS, so this change is recorded in Verso only and the customer&apos;s
              service is unchanged.
            </p>
          )}
          {skipped > 0 && (
            <p className="mt-1 text-sm font-medium text-amber-900">
              The steps marked <span className="font-bold">!</span> did not run, so this change is
              not in effect on those systems yet.
            </p>
          )}
          {verification?.status === 'confirmed' && (
            <p className="mt-1 text-sm text-green-800">
              Checked afterwards: {verification.confirmed} change{verification.confirmed === 1 ? '' : 's'} confirmed
              on the systems themselves.
            </p>
          )}
          {verification?.status === 'mismatch' && (
            <p className="mt-1 text-sm font-medium text-red-800">
              Read back afterwards, {verification.mismatched} change{verification.mismatched === 1 ? '' : 's'} had
              not actually taken. The steps marked <span className="font-bold">×</span> reported success but the
              system does not agree — treat this transition as incomplete.
            </p>
          )}
          {verification?.status === 'unavailable' && (
            <p className="mt-1 text-sm font-medium text-amber-900">
              {verification.confirmed > 0
                ? <>{verification.confirmed} change{verification.confirmed === 1 ? '' : 's'} confirmed, but {verification.unavailable} could not be checked.</>
                : <>None of this could be checked afterwards ({verification.unavailable} unverified).</>}
              {' '}The writes reported success; nothing has confirmed them.
            </p>
          )}
          {looseChecks.filter((c) => c.status === 'mismatch').map((c) => (
            <p key={c.describe} className="mt-1 text-sm font-medium text-red-800">{c.detail}</p>
          ))}
          {(plan.notes || []).map((n) => (
            <p key={n} className="mt-1 text-sm text-amber-800">{n}</p>
          ))}
        </div>
        <button
          onClick={onDismiss}
          className="rounded-md border border-amber-300 bg-white px-3 py-1 text-xs font-medium text-amber-700 hover:bg-amber-50"
        >
          Dismiss
        </button>
      </div>

      <div className="mt-4 space-y-3">
        {orderedSystems.map((sys) => (
          <div key={sys} className="rounded-md border border-amber-200 bg-white p-3">
            <div className="mb-2 flex items-center gap-2">
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${systemLabels[sys].cls}`}>
                {systemLabels[sys].label}
              </span>
              <span className="text-xs text-gray-500">
                {groups[sys].length} step{groups[sys].length === 1 ? '' : 's'}
              </span>
            </div>
            <ul className="space-y-1.5 text-sm">
              {groups[sys].map((step) => (
                <li key={step.id} className="flex items-start gap-2">
                  {(() => {
                    const res  = outcome[step.id];
                    const mark = MARK[res?.status]
                      || (step.autoExecutable
                        ? { icon: '·', cls: 'bg-gray-200 text-gray-500', title: 'Not attempted' }
                        : { icon: '·', cls: 'bg-gray-200 text-gray-500', title: 'Manual action required' });
                    return (
                      <span
                        className={`mt-0.5 inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${mark.cls}`}
                        title={res?.error ? `${mark.title}: ${res.error}` : mark.title}
                      >
                        {mark.icon}
                      </span>
                    );
                  })()}
                  <span className="text-gray-700">
                    {step.description}
                    {outcome[step.id]?.status === 'skipped' && (
                      <span className="ml-1 font-medium text-amber-900">— not run</span>
                    )}
                    {outcome[step.id]?.status === 'frozen' && (
                      <span className="ml-1 font-medium text-slate-800"
                            title={outcome[step.id].reason || undefined}>
                        — blocked by the refactor freeze; no write sent
                      </span>
                    )}
                    {outcome[step.id]?.error && (
                      <span className="ml-1 text-red-700">— {outcome[step.id].error}</span>
                    )}
                    {/* Only ever added to a step that otherwise looks fine. A
                        mismatch has already turned the step into an error above,
                        so this says "we looked" or "we couldn't". */}
                    {checkFor[step.id]?.status === 'confirmed' && (
                      <span className="ml-1 text-green-700">— confirmed on the system</span>
                    )}
                    {checkFor[step.id]?.status === 'unavailable' && (
                      <span className="ml-1 text-amber-900" title={checkFor[step.id].detail || undefined}>
                        — could not be verified
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function SubscriberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const canOp  = canOperate(role);   // edit contact, assign/unassign DID + location
  const { hostForDid, suspendedDestByHost } = usePbxNames();
  const canAdm = canAdmin(role);     // status transitions, delete

  const subQ = useQuery({
    queryKey: ['subscriber', id],
    queryFn: () => api.get(`/subscribers/${id}`),
  });

  // The subscriber detail response doesn't embed sip_accounts. Fetch
  // them separately from the org-scoped endpoint so imported extensions
  // show up on the SIP Accounts panel. (Store already filters by
  // subscriberId + hides soft-deleted rows.)
  const sipQ = useQuery({
    queryKey: ['subscriber', id, 'sip-accounts'],
    queryFn: () => api.get('/sip-accounts', { subscriberId: id }),
  });

  // LINES — what the customer actually buys. Each row already carries its number,
  // package, PBX host, extension, port and voicemail state, so the Lines tab
  // renders without a fan of follow-up requests.
  //
  // Read-only for now: `lines` is still a projection that migration 0042/0043
  // backfilled, and the dual-write is phase 3 of the refactor. Editing a number,
  // package or host continues to go through the DID / extension / tier endpoints.
  const linesQ = useQuery({
    queryKey: ['subscriber', id, 'lines'],
    queryFn: () => api.get(`/subscribers/${id}/lines`),
    // The list two operators are most likely to hold open together — one sells a line
    // while the other is on the phone to the same customer (Kevin, 2026-08-13).
    ...sharedList(),
  });
  const lines = linesQ.data?.lines ?? [];
  // Packages come from the tiersQ already declared below for the subscriber's tier
  // picker — same queryKey, so react-query serves both from one fetch. The per-line
  // feature gate reads it: what a line may do comes from what was sold, which is the
  // whole reason the package sits on the line.
  const [addingLine, setAddingLine] = useState(false);
  const [assigningDev, setAssigningDev] = useState(false);
  const [hostForLine, setHostForLine] = useState(null);      // line whose host is being set
  const [deletingLine, setDeletingLine] = useState(null);
  // Set when a delete is waiting on a teardown to finish first. Holds the line id, so a
  // dialog closed mid-run cannot leave a stale object queued for deletion.
  const [removeAfterTeardown, setRemoveAfterTeardown] = useState(null);
  // Building a line and taking it back down. One dialog, two directions — it shows the
  // plan before it runs and the per-step outcome with the read-back afterwards, which is
  // the whole reason these are not one-click actions.
  const [runForLine, setRunForLine] = useState(null);   // { line, mode }
  const [portForLine, setPortForLine] = useState(null); // line whose handset port is being set
  const invalidateLines = () => {
    queryClient.invalidateQueries({ queryKey: ['subscriber', id, 'lines'] });
    queryClient.invalidateQueries({ queryKey: ['subscriber', String(id)] });
    queryClient.invalidateQueries({ queryKey: ['dids'] });
  };
  const setLineHostM = useMutation({
    mutationFn: ({ lineId, agentId }) => api.put(`/lines/${lineId}/pbx-host`, { pbx_agent_id: agentId || null }),
    onSuccess: () => { setHostForLine(null); invalidateLines(); },
  });
  const setLineStatusM = useMutation({
    mutationFn: ({ lineId, status }) => api.patch(`/lines/${lineId}/status`, { status }),
    onSuccess: invalidateLines,
  });
  const deleteLineM = useMutation({
    mutationFn: (lineId) => api.delete(`/lines/${lineId}`),
    onSuccess: () => { setDeletingLine(null); invalidateLines(); },
  });
  // The five panels this tab used to show as flat siblings — numbers, routing,
  // extensions, voicemail, messaging. They are subscriber-wide views, so they sit
  // below the lines behind one control rather than five, until each is folded into
  // the line that owns it. Closed by default: the table plus a line's own panel
  // answers the ordinary questions.

  // Linked devices — one row per device the subscriber owns. The store
  // filters by subscriber_id + hides soft-deleted rows.
  const devicesQ = useQuery({
    queryKey: ['subscriber', id, 'devices'],
    queryFn: () => api.get('/devices', { subscriberId: id }),
  });

  // THE CALIX ROUTER'S LIVE STATE, for the Devices tab's Status column.
  //
  // `devices.status` is 'unknown' on every Calix and always will be: Verso learns a phone's status
  // from GDMS, and a Calix is not in GDMS. So the column had nothing to say about the one device on
  // the account that could answer for itself (Kevin, 2026-08-21). This is that answer.
  //
  // SAME QUERY KEY as CalixRouterCard below and as the line page's call path, so react-query serves
  // all three from one read and no two of them can disagree about whether the router is up. The
  // worker caches per router for five minutes because Calix forbids mass polling — that cache is
  // what makes showing this on render, rather than behind a button, safe at all.
  const calixDevice = (Array.isArray(devicesQ?.data) ? devicesQ.data : [])
    .find((d) => d.selfProvisioning && d.fsan);
  const calixHealthQ = useQuery({
    queryKey: ['subscriber', id, 'calix-health'],
    queryFn: () => api.get(`/subscribers/${id}/calix-health`),
    enabled: Boolean(calixDevice),
    staleTime: 300_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Service tier assignment (org_admin). Tiers themselves are defined in the
  // admin console; here we only pick one per subscriber.
  const [changingTier, setChangingTier] = useState(false);
  const tiersQ = useQuery({
    queryKey: ['service-tiers'],
    queryFn: () => api.get('/service-tiers'),
    staleTime: 5 * 60_000,
  });
  // SMS-to-email toggle (staff-side management, mirrors the portal self-service).

  // Per-DID SMS enable at Commio (each DID is its own messaging mailbox).

  const assignTier = useMutation({
    mutationFn: (tierId) => api.put(`/subscribers/${id}/service-tier`, { service_tier_id: tierId }),
    onSuccess: () => {
      setChangingTier(false);
      queryClient.invalidateQueries({ queryKey: ['subscriber', id] });
      queryClient.invalidateQueries({ queryKey: ['subscriber', String(id), 'voicemails'] });
    },
  });

  // Cloud-stored voicemail summary — drives the Voicemail tab count. Cheap D1
  // read, fetched eagerly so the tab badge is right before the tab is opened.

  // SMS metadata summary (staff sees counts/activity/opt-outs — never message
  // content, mirroring the voicemail privacy stance).

  // Aggregated call history across all extensions this subscriber owns.
  // Cloud fans out to the PBX agent(s) that host the subscriber's
  // sip_accounts, merges + sorts. Only enabled when the subscriber has
  // at least one active SIP account (nothing to aggregate otherwise).
  const hasSipAccounts = Array.isArray(sipQ.data) && sipQ.data.length > 0;
  // Call History time-window filter. Backend accepts `since` as epoch
  // seconds; each option maps to a rolling window relative to now.
  const CALL_WINDOWS = [
    { id: '1h',  label: '1 hr',    seconds: 3600 },
    { id: '24h', label: '24 hrs',  seconds: 86400 },
    { id: '3d',  label: '3 days',  seconds: 3 * 86400 },
    { id: '7d',  label: '7 days',  seconds: 7 * 86400 },
    { id: '30d', label: '30 days', seconds: 30 * 86400 },
  ];
  const [callWindow, setCallWindow] = useState('24h');
  const callsQ = useQuery({
    // Rounded epoch bucket in the key so the query key only changes
    // when the window changes (not on every render — otherwise every
    // second the query re-fires as `since` drifts).
    queryKey: ['subscriber', id, 'calls', callWindow],
    queryFn: () => {
      const win = CALL_WINDOWS.find((w) => w.id === callWindow) || CALL_WINDOWS[1];
      const since = Math.floor(Date.now() / 1000) - win.seconds;
      return api.get(`/subscribers/${id}/calls`, { limit: 50, since });
    },
    enabled: hasSipAccounts,
    refetchInterval: 60_000,
    retry: 1,
  });

  // AppShell publishes the WS-driven activeCalls map via Outlet context.
  // Filter to only calls whose extension matches one of this subscriber's
  // sip_accounts so the Live Calls tab shows just their in-flight calls.
  const { activeCalls: allActiveCalls = {} } = useOutletContext() || {};
  const subscriberExtensions = useMemo(
    () => new Set(
      (Array.isArray(sipQ.data) ? sipQ.data : [])
        .map((a) => String(a.username || '').trim())
        .filter(Boolean)
    ),
    [sipQ.data],
  );
  const subscriberActiveCalls = useMemo(() => {
    const out = {};
    for (const [k, v] of Object.entries(allActiveCalls)) {
      if (v.ext && subscriberExtensions.has(String(v.ext))) out[k] = v;
    }
    return out;
  }, [allActiveCalls, subscriberExtensions]);

  // Tab state, in the URL hash so a refresh or a shared link lands on the same tab.
  //
  // This page had the working version and the detail pages below it did not, so the logic
  // moved to hooks/useHashTab.js and they all import it. See that file for why plain
  // component state is wrong for a tab.
  //
  // DIDs, Routing, Extensions, Voicemail and Messaging stopped being tabs of their
  // own: every one of them is a property of a LINE, and splitting them across five
  // tabs meant the answer to "what does this number do" was never on one screen.
  // They now live inside the Lines tab. Old ids are aliased rather than dropped so
  // a bookmark or a shared #extensions link still lands somewhere sensible.
  const [activeTab, selectTab] = useHashTab('overview', {
    aliases: SUBSCRIBER_TAB_ALIASES,
    valid: SUBSCRIBER_TABS,
  });

  const preQ = useQuery({
    queryKey: ['subscriber', id, 'delete-preconditions'],
    queryFn: () => api.get(`/subscribers/${id}/delete-preconditions`),
    enabled: canAdm,
    // 4xx bodies are useful too, but errors here just gate the delete button
    // — swallow so a 404/etc. doesn't crash the whole page.
    retry: false,
  });

  const [editing, setEditing]           = useState(false);

  // (The account voicemail-settings query lived here for the card on the Users tab.
  // The card moved to each line's Voicemail tab on 2026-08-15 and this was its only
  // consumer, so the fetch went with it rather than staying as an orphan request on
  // every Users-tab open.)

  // Same queryKey as AccountUsersPanel, so react-query serves both from one
  // fetch — this is only here for the tab's count badge.
  const usersQ = useQuery({
    queryKey: ['subscriber', String(id), 'users'],
    queryFn: () => api.get(`/subscribers/${id}/users`),
  });

  // Pending work is reported PER LINE now, in the Lines table's "In sync" column and on
  // the line's own page. The customer-wide count and its one red button are gone: the
  // count could not say which of a customer's numbers was wrong, and the button swept
  // every number, extension and device they had. See components/lines/PendingChanges.jsx.
  const [pickingLoc, setPickingLoc]     = useState(false);
  // "Create a new address" from inside the picker — the common case when the
  // address genuinely isn't on file yet, which previously meant leaving the page.
  const [creatingLoc, setCreatingLoc]   = useState(false);

  // SIP-password reveal. Every reveal needs a fresh acknowledgement and is
  // audited; the surface label tells a reviewer it came from this page.
  const revealSecret = useRevealSecret('subscriber-extensions');
  const [pickedLocId, setPickedLocId]   = useState('');
  // Something that happened alongside what you asked for, and would otherwise live
  // only in an audit row — a carrier side-effect that failed, or a name it shortened.
  // Which DID row has its troubleshooting detail open. One at a time — this is a
  // lookup, not a comparison.
  const [confirmUnassignLoc, setConfirmUnassignLoc] = useState(null);
  const [confirmUnassignDid, setConfirmUnassignDid] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['subscriber', id] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
    queryClient.invalidateQueries({ queryKey: ['dids'] });
    queryClient.invalidateQueries({ queryKey: ['locations', 'unassigned'] });
    queryClient.invalidateQueries({ queryKey: ['dids', 'unassigned'] });
  };

  // Free-form tags (e.g. "xBar7" billing-provider flag).
  const [addingTag, setAddingTag] = useState(false);
  const [newTag, setNewTag] = useState('');
  const addTag = useMutation({
    mutationFn: (tag) => api.post(`/subscribers/${id}/tags`, { tag }),
    onSuccess: () => { invalidate(); setAddingTag(false); setNewTag(''); },
  });
  const removeTag = useMutation({
    mutationFn: (tag) => api.delete(`/subscribers/${id}/tags/${encodeURIComponent(tag)}`),
    onSuccess: () => invalidate(),
  });

  // Operator "view as subscriber" — mint a one-time portal handoff and open it
  // in a new tab. Pre-open the tab synchronously so the popup blocker allows it.
  const viewAsPortal = useMutation({
    mutationFn: async () => {
      const win = window.open('', '_blank');
      try {
        const res = await api.post(`/subscribers/${id}/portal-impersonate`);
        if (win) win.location = res.handoff_path;
        return res;
      } catch (err) {
        if (win) win.close();
        throw err;
      }
    },
  });

  // Pool queries — enabled only when the picker is open so we don't hit the
  // API on every mount.
  // Every location in the org, in one query. The picker groups them (free /
  // already-in-use / wrong-type) and filters client-side, so splitting this into
  // an `?unassigned=1` call plus an all-locations call bought nothing — and the
  // old pool-only version is why an address already serving someone simply
  // wasn't offered.
  const allLocQ = useQuery({
    queryKey: ['locations', 'assignable'],
    queryFn: () => api.get('/locations'),
    enabled: pickingLoc,
  });

  const patchSub = useMutation({
    mutationFn: (body) => api.patch(`/subscribers/${id}`, body),
    onSuccess: () => { invalidate(); setEditing(false); },
  });

  const [statusPlan, setStatusPlan] = useState(null);
  // Which transition the operator has asked for but not yet confirmed.
  const [pendingStatus, setPendingStatus] = useState(null);
  const changeStatus = useMutation({
    mutationFn: ({ status, allowedSystems }) => api.post(`/subscribers/${id}/status`, {
      status,
      // Sent explicitly now. The endpoint defaults to all three, so this changes nothing
      // unless the operator unticked something — but a plan that shows checkboxes and then
      // ignores them would be worse than showing none.
      allowed_systems: allowedSystems,
    }),
    onSuccess: (res) => {
      invalidate();
      // Server returns { ...updated, plan: {...} } — surface the fan-out
      // plan so the operator sees what still needs manual attention in
      // external systems (GDMS, Commio, PBX).
      // Keep the EXECUTION alongside the plan. This kept only `res.plan`, so the
      // panel could only ever show what was intended — it drew a green tick beside
      // every auto-executable step whether or not it ran. Suspension skipped every
      // PBX step for months behind those ticks (2026-08-02).
      if (res?.plan?.steps?.length) setStatusPlan({ ...res.plan, execution: res.execution });
      setPendingStatus(null);
    },
  });

  const assignLoc = useMutation({
    mutationFn: (locId) => api.put(`/locations/${locId}/subscriber/${id}`),
    onSuccess: () => { invalidate(); setPickingLoc(false); setPickedLocId(''); },
  });
  // Create an address and link it to this customer in one go.
  //
  // NO TYPE CHECK. This used to refuse an address whose type differed from the
  // customer's, on the grounds that Commio matches the 911 site type against the
  // caller-ID type. That match is real — but it is between the LINE'S PACKAGE and
  // the address, not the customer and the address, and `createLine` enforces it
  // where the package is known. A customer holding both a residential and a
  // business address is the ordinary shape of a home business, and this check
  // forbade exactly that.
  const createAndAssignLoc = useMutation({
    mutationFn: async (body) => {
      const created = await api.post('/locations', body);
      const newId = created?.id ?? created?.location?.id;
      if (!newId) throw new Error('Address was created but no id came back — assign it from the Locations page.');
      await api.put(`/locations/${newId}/subscriber/${id}`);
      return newId;
    },
    onSuccess: () => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setCreatingLoc(false); setPickingLoc(false); setPickedLocId('');
    },
  });

  const unassignLoc = useMutation({
    // Per-subscriber unlink: an address shared with another customer must keep
    // serving them. The bare DELETE .../subscriber empties the address entirely.
    mutationFn: (locId) => api.delete(`/locations/${locId}/subscriber/${id}`),
    onSuccess: () => { invalidate(); setConfirmUnassignLoc(null); },
  });
  // Assigning a number has SIDE EFFECTS at the carrier — a caller-ID profile, and
  // the number's host if it had none. Those can fail or be adjusted while the
  // assignment itself succeeds; the response says so and the page used to throw it
  // away. On 2026-08-02 a customer's caller-ID name was rejected by Commio for
  // being over 15 characters and nothing anywhere said so.
  const unassignDid = useMutation({
    mutationFn: (e164) => api.delete(`/dids/${encodeURIComponent(stripPlus(e164))}/subscriber`),
    onSuccess: () => { invalidate(); setConfirmUnassignDid(null); },
  });
  // ---- Basic vs complex service classification ----
  // Basic phone service = every DID lands directly on an extension
  // (FreePBX inbound route destination "from-did-direct,<ext>,1"). Anything
  // else — time conditions, queues, ring groups, IVRs — is a complex
  // configuration that Verso can't safely rewrite yet, so its routing
  // controls render read-only. If we can't reach the PBX to verify, we fail
  // safe and treat the config as complex.
  const primaryAgentId = subQ.data?.primary_agent_id || subQ.data?.primaryAgentId || null;
  // This classifier gates every routing control on the page, and it used to fail
  // OPEN in three ways — each of which told an operator "Basic service, safe to
  // edit" about a customer it had not actually looked at:
  //
  //   * no primary_agent_id  → 'basic' without checking anything;
  //   * it only ever read the PRIMARY host's routes, so a number delivered
  //     elsewhere was invisible;
  //   * a DID with no route on the host it checked counted as fine, when it really
  //     means "I never saw this number's call flow".
  //
  // Stone Mountain Lodge hit all three: both numbers land on time conditions on
  // PBX-02 while their extensions (and Verso's idea of the host) are on PBX-01, and
  // the page said Basic service. Not knowing is now 'unverified', which reads as
  // read-only — the same direction the provisioning planner takes.
  const didHosts = useMemo(() => {
    const subDids = Array.isArray(subQ.data?.dids) ? subQ.data.dids : [];
    return subDids.map((d) => ({ did: d, ...hostForDid(d) }));
  }, [subQ.data, hostForDid]);

  const routeHostIds = useMemo(() => {
    const ids = new Set(didHosts.map((h) => h.agentId).filter(Boolean));
    if (primaryAgentId && ids.size === 0) ids.add(primaryAgentId);
    return [...ids];
  }, [didHosts, primaryAgentId]);

  // One query per host the numbers actually reach, not just the primary.
  const routeQueries = useQueries({
    queries: routeHostIds.map((agentId) => ({
      queryKey: ['agent', agentId, 'inbound-routes'],
      queryFn: () => api.get(`/agents/${agentId}/proxy/inbound-routes`),
      staleTime: 60_000,
      retry: 1,
    })),
  });

  const { configClass } = useMemo(() => {
    const subDids = Array.isArray(subQ.data?.dids) ? subQ.data.dids : [];
    if (subDids.length === 0) return { configClass: 'basic', complexDids: {}, unroutedDids: {}, parkedDids: {}, suspendedDids: {} };
    if (routeHostIds.length === 0) return { configClass: 'unverified', complexDids: {}, unroutedDids: {}, parkedDids: {}, suspendedDids: {} };
    if (routeQueries.some((q) => q.isLoading)) return { configClass: 'pending', complexDids: {}, unroutedDids: {}, parkedDids: {}, suspendedDids: {} };

    // `<agentId>:<digits>` → destination, plus which hosts answered at all.
    const destByHostDid = {};
    const answered = new Set();
    routeHostIds.forEach((agentId, i) => {
      const q = routeQueries[i];
      if (!q || q.isError || !q.data) return;
      answered.add(agentId);
      const routes = Array.isArray(q.data) ? q.data : (q.data.routes || q.data.rows || []);
      for (const r of routes) {
        const key = String(r.did || r.extension || '').replace(/[^0-9]/g, '');
        if (key) destByHostDid[`${agentId}:${key}`] = String(r.destination || '');
      }
    });

    // Every number that appears in ANY answered host's table. A number missing
    // from its own host might still be routed on another one — that's the Stone
    // Mountain shape (numbers on PBX-02 while Verso thought PBX-01) and must not
    // read as "nothing here".
    const seenOnSomeHost = new Set(Object.keys(destByHostDid).map((k) => k.split(':')[1]));
    const everyHostAnswered = routeHostIds.every((id) => answered.has(id));

    const complex = {};
    const unrouted = {};
    const parked = {};
    const suspendedDids = {};
    // Numbers answered by an auto attendant. Kept apart from `complex` so a menu — which Verso
    // builds and manages — is not described as a flow it cannot touch.
    const attendant = {};
    let allSeen = true;
    for (const { did: d, agentId } of didHosts) {
      const key = String(d.e164 || '').replace(/[^0-9]/g, '');
      if (!agentId || !answered.has(agentId)) { allSeen = false; continue; }
      const dest = destByHostDid[`${agentId}:${key}`];
      if (dest == null) {
        // NOT unverifiable: we read this host's whole route table and the number
        // isn't in it. Two different things can be true, though —
        if (seenOnSomeHost.has(key) || !everyHostAnswered) {
          // — the flow may live on a host we didn't attribute it to. Locked, and
          // for a reason worth investigating rather than a shrug.
          allSeen = false;
        } else {
          // — or the number genuinely has no inbound route anywhere we can see.
          // A number with no flow cannot be carrying a complex one, and pointing
          // it at an extension is exactly the fix, so locking it was backwards:
          // a new DID added to a customer arrived read-only (ticket #17).
          unrouted[d.e164] = true;
        }
        continue;
      }
      // Destinations that do NOT make a config complex:
      //   from-did-direct,  a direct extension
      //   ext-group,        a ring group (Verso imports, edits and pushes these)
      //   app-blackhole,    PARKED — the call is answered and dropped. This is
      //                     FreePBX's Terminate Call, and it is where VERSO puts
      //                     a number on suspend or cancel. Calling it a foreign
      //                     flow locked an operator out of a spare DID with the
      //                     very state Verso had put it in (2026-08-01).
      // Everything else — time conditions, queues, IVRs — is still a flow Verso
      // can't reproduce.
      //   the host's suspended_destination — SUSPENDED. Verso wrote this itself
      //                     when the customer was suspended, so it is not a
      //                     foreign flow. Reading it as complex locked a
      //                     suspended customer's config read-only, meaning the
      //                     product could put an account into a state its own UI
      //                     then refused to edit (2026-08-02).
      const hostSuspendedDest = suspendedDestByHost[hostForDid(d).agentId] || null;
      if (hostSuspendedDest && dest === hostSuspendedDest) {
        suspendedDids[d.e164] = dest;
      } else if (dest.startsWith('app-blackhole,')) {
        parked[d.e164] = dest;
      } else if (dest.startsWith('ivr-')) {
        // AN AUTO ATTENDANT IS NOW ONE OF OURS. Verso creates menus, points numbers at them,
        // sets the greeting and reads the whole thing back (routes/ivrCreate.js, ivrPrompt.js,
        // lineRoute.js). Lumping it in with time conditions dated from before any of that
        // existed, and the moment a line was pointed at a menu the customer's header said
        // "Complex config · read-only" (2026-08-27).
        attendant[d.e164] = dest;
      } else if (!dest.startsWith('from-did-direct,') && !dest.startsWith('ext-group,')) {
        complex[d.e164] = dest;
      }
    }

    if (Object.keys(complex).length > 0) {
      return { configClass: 'complex', complexDids: complex, unroutedDids: unrouted, parkedDids: parked, suspendedDids };
    }
    return {
      configClass: allSeen ? 'basic' : 'unverified',
      complexDids: complex,
      unroutedDids: unrouted,
      parkedDids: parked,
      suspendedDids,
    };
  }, [subQ.data, didHosts, routeHostIds, routeQueries, suspendedDestByHost, hostForDid]);

  // "Add extension" modal (SIP Accounts tab), plus per-row edit/remove.
  // Point a DID at one of this subscriber's extensions (or clear back to
  // PBX-side routing). Server enforces the same-subscriber constraint.

  // Assign a device from the unassigned pool. The dropdown filters format-
  // agnostically on MAC (any separators) or model.

  // Assign a device from the unassigned pool. The dropdown filters format-agnostically on
  // MAC (any separators), model, or FSAN — the number printed on a Calix, which is the only
  // identifier that means anything to whoever is holding one.
  //
  // Restored 2026-08-13: the Devices tab BODY was deleted along with the "account-wide"
  // drawer, which sat next to it in the file. The tab and its count survived, so the page
  // reported "Devices 5" and rendered nothing — worse than removing the tab, because it
  // says the data is there and then does not show it.
  const [pickDevQuery, setPickDevQuery] = useState('');
  const [pickedDevId, setPickedDevId] = useState('');
  const poolDevQ = useQuery({
    queryKey: ['devices', 'unassigned'],
    queryFn: () => api.get('/devices', { unassigned: 1 }),
    enabled: activeTab === 'devices',
  });
  const assignDev = useMutation({
    mutationFn: (devId) => api.put(`/devices/${devId}/subscriber/${id}`),
    // Close on success, like every other add on this page. Leaving it open with the fields
    // cleared reads as "nothing happened" for an action that has just changed the customer.
    onSuccess: () => { invalidate(); setPickDevQuery(''); setPickedDevId(''); setAssigningDev(false); },
  });

  // Removing a device was only possible from the device's own page. Every other tab could
  // take a thing off the customer from the row it was on, so this one looked like devices
  // could not be removed at all (Kevin, 2026-08-13: "the same experience across the tabs
  // when adding and removing"). Same endpoint the device page calls — it also detaches the
  // bound SIP accounts, which is why the confirm says so.
  const [confirmUnassignDev, setConfirmUnassignDev] = useState(null);
  const unassignDev = useMutation({
    mutationFn: (devId) => api.delete(`/devices/${devId}/subscriber`),
    onSuccess: () => { invalidate(); setConfirmUnassignDev(null); },
  });

  const deleteSub = useMutation({
    mutationFn: () => api.delete(`/subscribers/${id}`, { confirm: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscribers'] });
      navigate('/subscribers');
    },
  });

  const sub = subQ.data;

  // What a detail page's back arrow should say and do to get the operator back HERE —
  // this customer, on this tab. Passed as router state on every link that leaves this page
  // for a line, device, address or number. Without it those pages fall back to their
  // hierarchical parent, which is the global list and drops the customer entirely.
  const backHere = backTo(sub?.name || 'Subscriber', `/subscribers/${id}#${activeTab}`);

  const breadcrumb = (
    <div className="text-sm text-gray-500">
      <Link to="/subscribers" className="text-blue-600 hover:underline">Subscribers</Link>
      <span className="mx-2">/</span>
      <span>{sub?.name || `#${id}`}</span>
    </div>
  );

  if (subQ.isLoading) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <LoadingSpinner message="Loading subscriber..." />
      </div>
    );
  }
  if (subQ.error) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {subQ.error?.body?.error || subQ.error.message || 'Failed to load subscriber'}
        </div>
      </div>
    );
  }
  if (!sub) {
    return (
      <div className="space-y-6">
        {breadcrumb}
        <div className="rounded-md border border-gray-200 bg-white p-8 text-center text-sm text-gray-500">
          Subscriber not found.
        </div>
      </div>
    );
  }

  const locations = Array.isArray(sub.locations) ? sub.locations : [];
  const dids      = Array.isArray(sub.dids)      ? sub.dids      : [];
  // Prefer the dedicated /sip-accounts?subscriberId= endpoint; fall back
  // to whatever the detail response embedded (some earlier responses
  // packed them under `.accounts`).
  const sipAccounts = Array.isArray(sipQ.data)
    ? sipQ.data
    : Array.isArray(sub.accounts) ? sub.accounts : [];

  // How many customers an address already serves.
  const locSubCount = (l) => {
    if (l.subscriberCount != null) return Number(l.subscriberCount);
    if (Array.isArray(l.subscribers)) return l.subscribers.length;
    return (l.subscriber?.id ?? l.subscriberId ?? l.subscriber_id) != null ? 1 : 0;
  };
  const allLocs = Array.isArray(allLocQ.data)
    ? allLocQ.data
    : Array.isArray(allLocQ.data?.rows) ? allLocQ.data.rows : [];

  // Which lifecycle transitions are legal from the current status. Mirrors
  // the server's validateTransition rules — a cancelled row cannot go back
  // to active (one-way per feedback_verso_soft_delete.md).
  const canReactivate = sub.status !== 'active' && sub.cancelledAt == null;
  // A subscriber with nothing attached can skip the cancelled/teardown step and
  // go straight to inactive (then delete). With resources still attached, the
  // direct shortcut is hidden — cancel first so external DIDs/devices are torn
  // down. Mirrors the server's validateTransition guard.
  const hasResources =
    (Array.isArray(sub.dids) ? sub.dids.length : 0) +
    (Array.isArray(sipQ.data) ? sipQ.data.length : 0) +
    (Array.isArray(devicesQ.data) ? devicesQ.data.length : 0) > 0;
  const nextStatusOptions = STATUS_OPTIONS.filter((s) => {
    if (s === sub.status) return false;
    if (s === 'active' && !canReactivate) return false;
    if (s === 'inactive' && sub.status !== 'cancelled' && hasResources) return false;
    // cancelled is always reachable (except from itself); server enforces the rest.
    return true;
  });

  const preReady = preQ.data?.ready === true;

  return (
    <div className="space-y-6">
      {breadcrumb}

      {/* Header */}
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Header order (ticket #3): Status → Name → Service tier → Tags.
                  Type moved to the Overview tab to keep this row focused. */}
              <StatusBadge status={statusVariant(sub.status)} label={sub.status} />
              <h1 className="text-xl font-bold text-gray-900">{sub.name || `#${sub.id}`}</h1>
              {/* Service tier — defined in the admin console, assigned here
                  (billing decides). Untiered = unrestricted legacy. */}
              {changingTier ? (
                <select
                  autoFocus
                  value={sub.service_tier?.id ?? ''}
                  onChange={(e) => assignTier.mutate(e.target.value === '' ? null : Number(e.target.value))}
                  onBlur={() => setChangingTier(false)}
                  disabled={assignTier.isPending}
                  className="rounded-full border border-indigo-300 px-2 py-0.5 text-xs"
                >
                  <option value="">No tier (unrestricted)</option>
                  {(tiersQ.data?.tiers || []).map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => canAdm && setChangingTier(true)}
                  disabled={!canAdm}
                  title={canAdm ? 'Change service tier' : 'Service tier (org admins can change it)'}
                  className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    sub.service_tier ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                  } ${canAdm ? 'hover:ring-1 hover:ring-indigo-300' : 'cursor-default'}`}
                >
                  {sub.service_tier ? sub.service_tier.name : 'No tier'}
                </button>
              )}
              {configClass === 'basic' && dids.length > 0 && (
                <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700" title="All DIDs route directly to extensions — safe to edit here">
                  Basic service
                </span>
              )}
              {/* "read-only" WAS NOT TRUE. `configClass` drives these three badges and gates
                  no control on this page — nothing was ever locked. It also fired for an auto
                  attendant, which Verso now builds and manages, so pointing a number at a menu
                  labelled the whole customer read-only (2026-08-27). What IS true is the
                  Worker-side refusal: a build will not overwrite a call flow Verso did not
                  build. That is what this says now. */}
              {configClass === 'complex' && (
                <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800" title="A call flow built directly on the phone system — Verso will not overwrite it">
                  Custom call flow
                </span>
              )}
              {configClass === 'unverified' && (
                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600" title="Couldn't reach the PBX to verify routing — config locked as a precaution">
                  Routing unverified · locked
                </span>
              )}
              {(Array.isArray(sub.tags) ? sub.tags : []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 rounded-full bg-purple-100 py-0.5 pl-2 pr-1 text-xs font-medium text-purple-700">
                  {t}
                  {canOp && (
                    <button
                      onClick={() => removeTag.mutate(t)}
                      disabled={removeTag.isPending}
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-purple-500 hover:bg-purple-200 hover:text-purple-900 disabled:opacity-40"
                      title={`Remove tag "${t}"`}
                      aria-label={`Remove tag ${t}`}
                    >×</button>
                  )}
                </span>
              ))}
              {canOp && (
                addingTag ? (
                  <input
                    autoFocus
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && newTag.trim()) addTag.mutate(newTag.trim()); if (e.key === 'Escape') { setAddingTag(false); setNewTag(''); } }}
                    onBlur={() => { setAddingTag(false); setNewTag(''); }}
                    placeholder="tag + Enter"
                    className="w-28 rounded-full border border-gray-300 px-2 py-0.5 text-xs focus:border-purple-500 focus:outline-none"
                  />
                ) : (
                  <button onClick={() => setAddingTag(true)} className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-500 hover:border-purple-400 hover:text-purple-600">+ tag</button>
                )
              )}
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {/* Account = billing account #, defaulting to the subscriber id
                  when unset (ticket #3). The synthetic sub-NN extension is not
                  shown for customer subscribers — it read as confusing. */}
              Account <span className="font-mono">{sub.billingAccount || `#${sub.id}`}</span>
              {/* NO subscriber-level PBX. The DID owns the host — a customer whose
                  numbers are delivered to PBX-02 read "PBX PBX-01" here off
                  `primary_agent_id`, which is a fallback for objects that need a
                  host before any number does, not a fact about the customer. It is
                  shown per NUMBER on the DIDs and Routing tabs, and nowhere else. */}
            </p>
          </div>
          {(canOp || canAdm) && (
            <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
              {/* View Subscriber Portal stays a first-class button (ticket #3);
                  everything else folds into the kebab like the rest of the UI. */}
              {canOp && sub.status !== 'cancelled' && sub.features?.portal_access !== false && (
                <button
                  onClick={() => viewAsPortal.mutate()}
                  disabled={viewAsPortal.isPending}
                  title="Open this subscriber's self-service portal exactly as they see it (audited)."
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {viewAsPortal.isPending ? 'Opening…' : 'View Subscriber Portal'}
                </button>
              )}
              <ActionMenu
                title={`Actions for ${sub.name || `#${sub.id}`}`}
                items={[
                  ...(canOp ? [{
                    label: 'Edit',
                    onClick: () => { setEditing(true); patchSub.reset(); },
                  }] : []),

                  // Each legal lifecycle transition as its own item.
                  // PROPOSES, does not execute. See StatusChangeDialog — this called
                  // `mutate` straight from the menu, so cancelling a customer was one click
                  // and no question asked.
                  ...(canAdm ? nextStatusOptions.map((s) => ({
                    label: s === 'active' ? 'Reactivate' : `Change status → ${s}`,
                    danger: s === 'cancelled',
                    disabled: changeStatus.isPending,
                    onClick: () => { setPendingStatus(s); changeStatus.reset(); },
                  })) : []),
                  ...(canAdm ? [
                    { divider: true },
                    {
                      label: 'Delete',
                      danger: true,
                      disabled: !preReady,
                      title: preReady
                        ? 'Soft-delete this subscriber'
                        : (preQ.data?.blockers?.map((b) => b.message).join(' · ')
                           || 'Preconditions not yet met (status must be inactive, no active DIDs / accounts / devices)'),
                      onClick: () => { if (preReady) { setConfirmDelete(true); deleteSub.reset(); } },
                    },
                  ] : []),
                ]}
              />
            </div>
          )}
        </div>
        {changeStatus.isError && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {changeStatus.error?.body?.error || changeStatus.error?.message || 'Status change failed'}
          </p>
        )}
        {deleteSub.isError && (
          <p className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {deleteSub.error?.body?.error || deleteSub.error?.message || 'Delete failed'}
          </p>
        )}
      </div>

      {statusPlan && (
        <StatusFanOutPanel plan={statusPlan} onDismiss={() => setStatusPlan(null)} />
      )}


      {/* Tab navigation. Onboarding badges (!) render on tabs whose
          underlying count is zero — mirrors the pbx-portal prototype's
          "hey, come fill this in" cue. Live Calls tab pulses when >0. */}
      {(() => {
        const locCount = Array.isArray(sub.locations) ? sub.locations.length : 0;
        const devCount = Array.isArray(devicesQ.data) ? devicesQ.data.length : 0;
        // Live calls + call history intentionally not their own tabs —
        // they render inline on the Overview tab so a single page shows
        // current + historical activity at a glance.
        //
        // DID, extension and voicemail counts used to badge their own tabs. Those
        // tabs are gone; the counts now sit on the collapsed sub-sections inside
        // Lines, computed there so there is one source per badge rather than a
        // second copy to drift.
        const userCount = usersQ.data?.users?.length;
        const lineCount = linesQ.data ? lines.length : undefined;
        // The five top-level objects a subscriber HAS. Nothing else belongs at this
        // level: DIDs, routing, extensions, voicemail and messaging are all
        // properties of a line, and giving each its own tab meant "what does this
        // number actually do" was never answerable on one screen.
        const tabs = [
          { id: 'overview',   label: 'Overview' },
          // Users sits second, right after Overview (ticket #13). It was buried at
          // the bottom of Overview — which is why "there's no way to manage users
          // per business account" was a reasonable reading of the UI — and then
          // parked after Devices, behind the infrastructure. Who's on the account
          // is closer to who the customer IS than to what hardware they have.
          { id: 'users',      label: 'Users',      count: userCount },
          { id: 'locations',  label: 'Locations',  count: locCount },
          { id: 'lines',      label: 'Lines',      count: lineCount },
          // NO COUNT BADGE, deliberately. A zero count renders the amber "!" — which is right for
          // a customer with no line and wrong here, because most customers legitimately have no
          // recordings and a wall of amber trains an operator to ignore the marker.
          { id: 'recordings', label: 'Recordings' },
          { id: 'devices',    label: 'Devices',    count: devCount },
        ];
        return (
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex flex-wrap gap-4" role="tablist">
              {tabs.map((t) => {
                const isActive = activeTab === t.id;
                return (
                  <button
                    key={t.id}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => selectTab(t.id)}
                    className={`inline-flex items-center gap-2 border-b-2 px-1 pb-2 text-sm font-medium transition-colors ${
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {t.label}
                    {t.count === 0 && (
                      <span
                        title="Nothing here yet — click to add"
                        className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700"
                      >!</span>
                    )}
                    {t.count > 0 && (
                      <span className="rounded-full bg-gray-100 px-1.5 text-xs font-medium text-gray-600">{t.count}</span>
                    )}
                    {t.live > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-1.5 py-0.5 text-xs font-medium text-green-700">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" />
                        {t.live}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        );
      })()}

      {/* Overview */}
      {activeTab === 'overview' && (
      <Section title="Overview" subtitle="Identity and contact details for this customer.">
        <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Phone"   value={sub.phone} />
          <Field label="Email"   value={sub.email} />
          <Field label="Billing" value={sub.billingAccount} />
          <Field label="Type"    value={<TypePill type={sub.type} />} />
          <Field label="Created" value={fmtDate(sub.createdAt)} />
          {sub.cancelledAt && (
            <Field label="Cancelled" value={fmtDate(sub.cancelledAt)} />
          )}
        </dl>
        {sub.notes && (
          <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-700 whitespace-pre-wrap">
            {sub.notes}
          </div>
        )}
        <div className="mt-4 border-t border-gray-100 pt-3 text-sm">
          <span className="text-gray-500">Portal login:</span>{' '}
          {sub.portalEmail ? (
            <>
              <span className="font-mono text-gray-800">{sub.portalEmail}</span>
              {sub.portalLoginAt && (
                <span className="ml-2 text-xs text-gray-400">
                  last sign-in {fmtDateTime(sub.portalLoginAt)}
                </span>
              )}
            </>
          ) : (
            <span className="text-gray-400">Not enabled</span>
          )}
          {sub.features?.portal_access === false && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700"
              title="Portal access is not included in this subscriber’s service tier">
              not in tier
            </span>
          )}
        </div>
        {sub.calixFsan && <CalixRouterCard subscriberId={id} fsan={sub.calixFsan} />}
      </Section>
      )}

      {/* Messaging (SMS) config — staff-managed. Metadata only: message content
          is private to the subscriber's portal. SMS availability is a service-tier
          feature (set in the admin console); the SMS-to-email toggle is managed
          here or by the subscriber in their portal. */}

      {/* Locations */}
      {activeTab === 'locations' && (
      <Section
        title="Service addresses"
        subtitle={locations.length === 0
          ? 'No service address yet — assign one so 911 dispatch has somewhere to send help.'
          : `${locations.length} address${locations.length === 1 ? '' : 'es'} on this account.`}
        actions={canOp && !pickingLoc && (
          <AddButton onClick={() => { setPickingLoc(true); assignLoc.reset(); }}>
            Add address
          </AddButton>
        )}
      >
        {/* IN A MODAL, like every other add on this page. It expanded the section, which
            pushed the address list down the page as the operator went looking for it. */}
        {pickingLoc && (
          <FormModal
            title="Add a service address"
            subtitle="Pick one this customer already has, or create a new one. It is the 911 answer for every line that uses it."
            onClose={() => { setPickingLoc(false); setPickedLocId(''); assignLoc.reset(); }}
          >
            <LocationAssignPicker
              locations={allLocs}
              subscriber={{ id: sub.id, name: sub.name, type: sub.type }}
              assignedIds={locations.map((l) => l.id)}
              isLoading={allLocQ.isLoading}
              value={pickedLocId}
              onChange={setPickedLocId}
              onSubmit={() => assignLoc.mutate(pickedLocId)}
              onCancel={() => { setPickingLoc(false); setPickedLocId(''); assignLoc.reset(); }}
              onCreateNew={() => setCreatingLoc(true)}
              isPending={assignLoc.isPending}
              error={assignLoc.isError ? (assignLoc.error?.body?.error || assignLoc.error?.message || 'Assign failed') : null}
            />
          </FormModal>
        )}
        {locations.length > 0 && (
          <div className="mt-3 overflow-visible rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2">Label</th>
                  <th className="px-3 py-2">Address</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">E911</th>
                  {canOp && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {locations.map((l) => {
                  const addr = [l.addressLine1 || l.address_line1, l.city, l.state]
                    .filter(Boolean).join(', ');
                  return (
                    <tr key={l.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-medium">
                        <Link to={`/locations/${l.id}`} state={backHere} className="text-blue-600 hover:underline">
                          {l.label || `Location #${l.id}`}
                        </Link>
                        {/* Warn before someone edits an address another customer
                            also depends on for 911 dispatch. */}
                        {locSubCount(l) > 1 && (
                          <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 ring-1 ring-inset ring-amber-200"
                                title="This address also serves another customer. Editing it affects both.">
                            shared with {locSubCount(l) - 1} other{locSubCount(l) - 1 === 1 ? '' : 's'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600">{addr || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2"><TypePill type={l.type} /></td>
                      <td className="px-3 py-2">
                        {/* E911 address registration, NOT SIP. Commio is the
                            source of truth: e911Registered comes from its
                            usedBy/subscriberID/erlID rule. erlId alone is an
                            enterprise-ERL concept residential sites never carry,
                            so the old fallback mislabelled most of them. When
                            Commio can't be reached the field is absent — say
                            "unknown" rather than asserting a 911 gap. */}
                        {l.e911Registered === true
                          ? <StatusBadge status="active" label="E911 registered" />
                          : l.e911Registered === false
                            ? <StatusBadge status="inactive" label="E911 not registered" />
                            : (l.erlId || l.erl_id)
                              ? <StatusBadge status="active" label="E911 registered" />
                              : <StatusBadge status="unknown" label="E911 unknown" />}
                      </td>
                      {canOp && (
                        <td className="px-3 py-2 text-right">
                          {/* A kebab, like every other row on this page. This was a bare red
                              "Unassign" link — the only remove control on the subscriber that
                              was not in the menu, and the only one that fired straight off a
                              single click of a word sitting in the table. */}
                          <ActionMenu
                            title={`Actions for ${l.label || `Location #${l.id}`}`}
                            items={[{
                              label: 'Remove from this customer',
                              danger: true,
                              onClick: () => setConfirmUnassignLoc(l),
                            }]}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      )}

      {/* DIDs */}
      {/* LINES.
          The table first — one row per thing the customer buys, which is the answer
          to "what do they have" that used to require visiting four tabs.
          Then the detail panels, each of which is a property of a line: the numbers
          themselves, what an inbound call does, how a PBX realises it, the mailbox,
          and messaging. */}
      {activeTab === 'lines' && (
        /* ADDING A LINE WAS ONLY POSSIBLE TO A CUSTOMER WHO HAD NONE.

           The button lived exclusively in the empty state — `if (!lines.length)` in
           LinesTable — so the moment a customer had one line there was no way to sell them a
           second, from anywhere in the product. The subscriber's own action menu offers Edit,
           suspend, cancel and Delete, and nothing else creates a line. Found trying to rebuild
           Lyons Fire's station number after its teardown: they still had two other lines, so
           the only path to the wizard had vanished (Kevin, 2026-08-13).

           Here instead, where it is available whether the list is empty or not. The empty
           state keeps its own copy — with nothing on the tab, a centred call to action reads
           better than a button in a corner. */
        <Section
          title="Lines"
          subtitle="A line is a number plus the package sold with it. Everything PBX-side derives from the line's PBX assignment."
          actions={canAdm && lines.length > 0 && !addingLine && (
            <AddButton onClick={() => setAddingLine(true)}>Add a line</AddButton>
          )}
        >
          <div className="mt-4">
            <LinesTable
              lines={lines}
              loading={linesQ.isLoading}
              error={linesQ.error}
              canAdd={canAdm}
              onAdd={() => setAddingLine(true)}
              navigate={navigate}
              onSetHost={canAdm ? (l) => { setLineHostM.reset(); setHostForLine(l); } : undefined}
              onSetStatus={canAdm ? (l, status) => setLineStatusM.mutate({ lineId: l.id, status }) : undefined}
              onDelete={canAdm ? (l) => { deleteLineM.reset(); setDeletingLine(l); } : undefined}
              onBuild={canAdm ? (l) => setRunForLine({ line: l, mode: 'provision' }) : undefined}
              onTakeDown={canAdm ? (l) => setRunForLine({ line: l, mode: 'teardown' }) : undefined}
              onSetPort={canAdm ? (l) => setPortForLine(l) : undefined}
              backHere={backHere}
            />
          </div>
        </Section>
      )}

      {runForLine && (
        <LineRunDialog
          mode={runForLine.mode}
          line={runForLine.line}
          onClose={() => { setRunForLine(null); setRemoveAfterTeardown(null); }}
          // Refetch rather than close: the operator should see the outcome and the
          // updated row, not be thrown back to a stale table.
          onDone={(res) => {
            linesQ.refetch();
            queryClient.invalidateQueries({ queryKey: ['subscriber', String(id)] });
            // ONLY ON A TEARDOWN THAT CONFIRMED. `res.ok` is the run engine's own verdict —
            // every step did what it said AND read back clean. A partial or frozen run
            // leaves the line in place, because deleting the record that explains a
            // half-torn-down line is how an extension outlives the thing describing it.
            if (removeAfterTeardown && res?.ok) deleteLineM.mutate(removeAfterTeardown);
            setRemoveAfterTeardown(null);
          }}
        />
      )}

      {portForLine && (
        <SetDevicePortDialog
          line={portForLine}
          subscriberId={id}
          onClose={() => setPortForLine(null)}
          onSaved={() => { setPortForLine(null); linesQ.refetch(); }}
        />
      )}

      {activeTab === 'lines' && setLineStatusM.isError && (
        <p className="text-sm text-red-600">
          {setLineStatusM.error?.body?.error || setLineStatusM.error?.message || 'Could not change the line status'}
        </p>
      )}
      {/* A status change on a provisioned line is intent, not effect — the phone
          keeps working until provisioning runs. Saying so is the whole lesson of
          suspension reporting success for months while never reaching a PBX. */}
      {activeTab === 'lines' && setLineStatusM.data?.service_unchanged && (
        <p className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-700">
          Recorded in Verso. This line is still provisioned, so the extension stays
          registered and the number keeps ringing until a provisioning run carries the
          change to the PBX and the carrier.
        </p>
      )}

      {activeTab === 'lines' && hostForLine && (
        <SetLineHostModal
          line={hostForLine}
          saving={setLineHostM.isPending}
          error={setLineHostM.isError ? (setLineHostM.error?.body?.error || setLineHostM.error?.message) : null}
          onSave={(agentId) => setLineHostM.mutate({ lineId: hostForLine.id, agentId })}
          onClose={() => setHostForLine(null)}
        />
      )}

      {activeTab === 'lines' && deletingLine && (
        <ConfirmDeleteDialog
          title={`Delete line ${deletingLine.number ? fmtDid(deletingLine.number) : deletingLine.id}`}
          // DELETE MEANS DELETE, INCLUDING ON A LIVE LINE.
          //
          // This offered the bare row-delete whatever the line's state, so on a provisioned
          // line the server refused every time — removing the record would strand a
          // registered extension and a ringing number. Kevin hit it on line 63 (2026-08-12):
          // "I should be able to delete a line and it should do all the things to delete it
          // and place back in inventory. all the primitives for deleting a line should
          // work?" They do. They were simply never wired together here, only on the line
          // page — so the same action worked on one screen and refused on another.
          //
          // A live line now runs the teardown FIRST and is removed only if that succeeds,
          // through the run dialog this page already has, which shows the plan before it
          // touches anything. No second implementation of the teardown: the chain is
          // `setRemoveAfterTeardown` + the existing LineRunDialog, same as LineDetail.
          body={deletingLine.provisioned
            ? `${deletingLine.number ? fmtDid(deletingLine.number) : 'This line'} is live on `
              + `${deletingLine.pbxName || 'its PBX'}. It will be taken off the PBX, its phone `
              + 'and the carrier first, and removed only if that succeeds. The number returns '
              + 'to inventory and stays yours; voicemail is kept.'
            : `This removes the line and returns ${deletingLine.number ? fmtDid(deletingLine.number) : 'its number'} to inventory. It does not release the number at the carrier.`}
          confirmLabel={deletingLine.provisioned ? 'Take down and remove' : 'Delete line'}
          isPending={deleteLineM.isPending}
          error={deleteLineM.isError ? (deleteLineM.error?.body?.error || deleteLineM.error?.message) : null}
          onConfirm={() => {
            if (deletingLine.provisioned) {
              // Show the teardown plan, run it, and delete on the way out. The delete is
              // NOT fired here: a removal that raced the teardown would be the half-built
              // state the whole run engine exists to avoid.
              const line = deletingLine;
              setDeletingLine(null);
              setRemoveAfterTeardown(line.id);
              setRunForLine({ line, mode: 'teardown' });
              return;
            }
            deleteLineM.mutate(deletingLine.id);
          }}
          onCancel={() => setDeletingLine(null)}
        />
      )}

      {activeTab === 'lines' && addingLine && (
        <AddLineModal
          subscriberId={id}
          subscriberName={sub.name || sub.displayName || sub.display_name}
          onClose={() => setAddingLine(false)}
          onCreated={() => {
            setAddingLine(false);
            // The line claims a number, so the DID lists change too.
            queryClient.invalidateQueries({ queryKey: ['subscriber', id, 'lines'] });
            queryClient.invalidateQueries({ queryKey: ['subscriber', String(id)] });
            queryClient.invalidateQueries({ queryKey: ['dids'] });
          }}
        />
      )}

      {/* WHAT THE HIDDEN DRAWER WAS THE ONLY WAY TO SEE.
          =============================================
          A link here read "Show account-wide numbers, routing, extensions and messaging" and
          opened five sections scoped to the CUSTOMER: every number, every extension, the live
          call path for each, voicemail and SMS. Kevin, 2026-08-13: get rid of it and fold it
          into the normal workflow of adding and removing a line.

          Every one of those now lives where the operator is already standing:

            Numbers        the Lines table above — a line IS a number plus a package, and the
                           carrier detail is on the number's own page
            Call routing   the line's Routing tab, under the route type it should agree with
            Extensions     the line's Routing tab, which can now CREATE one as well as attach
            Voicemail      the line's Voicemail tab
            Messaging      the line's Messaging tab

          What does NOT fit on a line is what belongs to no line, and the drawer was the only
          place it showed. Deleting it silently would have hidden that instead of moving it,
          so it surfaces here as work to do rather than as a table to browse. */}
      {activeTab === 'lines' && (
        <UnattachedThings
          dids={dids}
          lines={lines}
          sipAccounts={sipAccounts}
          subscriberId={id}
          backHere={backHere}
          canAdd={canAdm}
          onAddLine={() => setAddingLine(true)}
        />
      )}

      {/* Live Calls — filtered by extensions this subscriber owns.
          Rendered inline on the Overview tab (no dedicated tab). */}
      {activeTab === 'overview' && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold text-gray-900">Live Calls</div>
              <div className="text-xs text-gray-500">
                Real-time state via WebSocket, across every PBX host, scoped to this
                subscriber&rsquo;s extensions.
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
              Object.keys(subscriberActiveCalls).length > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${Object.keys(subscriberActiveCalls).length > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
              {Object.keys(subscriberActiveCalls).length} active
            </span>
          </div>
          {Object.keys(subscriberActiveCalls).length === 0 ? (
            <div className="px-4 py-6 text-sm text-gray-400">No calls in progress on this subscriber&rsquo;s extensions.</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">State</th>
                  <th className="px-4 py-2 text-left font-medium">PBX</th>
                  <th className="px-4 py-2 text-left font-medium">Direction</th>
                  <th className="px-4 py-2 text-left font-medium">Ext</th>
                  <th className="px-4 py-2 text-left font-medium">From</th>
                  <th className="px-4 py-2 text-left font-medium">To</th>
                  <th className="px-4 py-2 text-left font-medium">Started</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {Object.values(subscriberActiveCalls)
                  .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))
                  .map((r) => {
                    const stateCls = {
                      dialing:  'bg-gray-100 text-gray-700',
                      ringing:  'bg-amber-100 text-amber-800',
                      answered: 'bg-blue-100 text-blue-700',
                      bridged:  'bg-green-100 text-green-700',
                    }[r.state] || 'bg-gray-100 text-gray-700';
                    return (
                      <tr key={r.key}>
                        <td className="px-4 py-2">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${stateCls}`}>
                            {r.state || 'unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-gray-600">{r.agentName || '—'}</td>
                        <td className="px-4 py-2 text-gray-600">{r.direction || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{r.ext || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{r.from || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs">{r.to   || '—'}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-xs text-gray-500">
                          {/* NOT parseTs — epoch milliseconds from AppShell's WebSocket
                              handler, not a D1 string. See the Live Calls card on Overview. */}
                          {r.startedAt
                            ? new Date(r.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Account users — who can sign in and who gets voicemail by email. Its own
          tab since 2026-07-30; it was the last card on Overview and effectively
          invisible. */}
      {activeTab === 'users' && (
        <AccountUsersPanel subscriberId={id} canOperate={canOp} />
      )}

      {activeTab === 'recordings' && (
        <RecordingsTab subscriberId={sub.id} canWrite={role === 'org_admin'} />
      )}

      {activeTab === 'devices' && (
      <Section
        title="Devices"
        subtitle={(() => {
          const list = Array.isArray(devicesQ.data) ? devicesQ.data : [];
          return list.length === 0
            ? 'No devices linked to this subscriber yet.'
            : `${list.length} device${list.length === 1 ? '' : 's'} linked.`;
        })()}
        /* THE ONLY TAB WITH NO BUTTON AT ALL. Its picker — a filter box, a select and an
           Assign button — sat open on the tab permanently, whether or not anybody was
           assigning anything, so the list of devices started halfway down the card. Now it
           is behind the same trigger as the other three (Kevin, 2026-08-13). */
        actions={canOp && !assigningDev && (
          <AddButton onClick={() => { setAssigningDev(true); assignDev.reset(); }}>
            Assign a device
          </AddButton>
        )}
      >
        {canOp && assigningDev && (() => {
          const hex = (x) => String(x || '').replace(/[^0-9a-f]/gi, '').toLowerCase();
          const pool = Array.isArray(poolDevQ.data) ? poolDevQ.data : [];
          const q = pickDevQuery.trim().toLowerCase();
          const qHex = hex(q);
          // Calix routers are identified by FSAN — that's what's printed on the
          // unit and what Calix Cloud keys on — so searching and labelling by MAC
          // alone made a Calix in the pool effectively invisible: typing its FSAN
          // matched nothing, and it listed under a MAC the operator has no reason
          // to recognise.
          const matches = pool.filter((d) => {
            const mac = String(d.mac || d.mac_address || '').toLowerCase();
            const model = String(d.model || '').toLowerCase();
            const fsan = String(d.fsan || '').toLowerCase();
            if (!q) return true;
            return mac.includes(q) || model.includes(q) || fsan.includes(q)
              || (qHex.length >= 2 && hex(mac).includes(qHex));
          }).slice(0, 200);
          // Lead with whichever identifier names the thing in the operator's hand.
          const devLabel = (d) => {
            const isCalix = String(d.deviceType ?? d.device_type ?? '').toLowerCase() === 'calix';
            const primary = isCalix ? (d.fsan || d.mac || d.mac_address) : (d.mac || d.mac_address || d.fsan);
            return [primary || '—', d.model, isCalix ? 'Calix' : null].filter(Boolean).join(' · ');
          };
          const close = () => { setAssigningDev(false); setPickedDevId(''); setPickDevQuery(''); assignDev.reset(); };
          return (
            <FormModal
              title="Assign a device"
              subtitle="Devices in inventory — not yet linked to any customer."
              onClose={close}
              footer={(
                <>
                  <button
                    onClick={() => pickedDevId && assignDev.mutate(pickedDevId)}
                    disabled={!pickedDevId || assignDev.isPending}
                    className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {assignDev.isPending ? 'Assigning…' : 'Assign device'}
                  </button>
                  <button
                    onClick={close}
                    className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  {assignDev.isError && (
                    <span className="text-xs text-red-600">{assignDev.error?.message || 'Assign failed'}</span>
                  )}
                </>
              )}
            >
              <input
                value={pickDevQuery}
                autoFocus
                onChange={(e) => setPickDevQuery(e.target.value)}
                placeholder="Filter pool by MAC, FSAN or model…"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
              />
              <select
                value={pickedDevId}
                onChange={(e) => setPickedDevId(e.target.value)}
                className="mt-3 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">
                  {poolDevQ.isLoading ? 'Loading pool…' : `Select device… (${matches.length} available)`}
                </option>
                {matches.map((d) => (
                  <option key={d.id} value={d.id}>{devLabel(d)}</option>
                ))}
              </select>
            </FormModal>
          );
        })()}
        {devicesQ.isLoading ? (
          <p className="mt-3 text-sm text-gray-400">Loading…</p>
        ) : Array.isArray(devicesQ.data) && devicesQ.data.length > 0 && (
          <div className="mt-3 overflow-visible rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Model</th>
                  <th className="px-3 py-2">MAC</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Location</th>
                  {canOp && <th className="px-3 py-2" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {devicesQ.data.map((d) => {
                  const mac = d.mac || d.mac_address;
                  const locId = d.resolvedLocationId ?? d.locationId ?? d.location_id;
                  const locLabel = d.locationLabel || d.location_label || d.locationAddress || d.location_address;
                  return (
                    <tr key={d.id ?? mac} className="hover:bg-gray-50">
                      <td className="px-3 py-2">
                        {/* A CALIX ANSWERS FOR ITSELF. `devices.status` is learned from GDMS, which
                            has never heard of a Calix, so it reads 'unknown' on this row forever —
                            and 'unknown' beside a router that is up and carrying calls is the kind
                            of warning that teaches an operator to stop reading the column. Matched
                            by FSAN because the health read answers for one router. */}
                        {(() => {
                          const ch = d.selfProvisioning && d.fsan
                            && calixHealthQ.data?.fsan
                            && String(calixHealthQ.data.fsan).toUpperCase() === String(d.fsan).toUpperCase()
                            ? calixHealthQ.data : null;
                          if (d.selfProvisioning && calixHealthQ.isFetching && !ch) {
                            return <StatusBadge status="unknown" label="asking Calix…" />;
                          }
                          if (ch?.health) {
                            return (
                              <StatusBadge
                                status={ch.health.online ? 'active' : 'critical'}
                                label={ch.health.online ? 'online' : 'offline'}
                              />
                            );
                          }
                          // Calix was asked and could not answer. Say so rather than fall back to
                          // the GDMS column, which would report 'unknown' as if nobody had looked.
                          if (ch?.error) {
                            return (
                              <StatusBadge
                                status="unknown"
                                label={ch.rate_limited ? 'rate-limited' : 'Calix unreachable'}
                              />
                            );
                          }
                          return (
                            <StatusBadge
                              status={d.status === 'online' ? 'active' : d.status === 'offline' ? 'critical' : 'unknown'}
                              label={d.status || 'unknown'}
                            />
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2">{d.model || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {mac ? (
                          <Link to={`/devices/${encodeURIComponent(mac)}`} state={backHere} className="text-blue-600 hover:underline">
                            {mac}
                          </Link>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2">{d.name || <span className="text-gray-300">—</span>}</td>
                      <td className="px-3 py-2">
                        {locId
                          ? <Link to={`/locations/${locId}`} state={backHere} className="text-blue-600 hover:underline">{locLabel || `#${locId}`}</Link>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      {canOp && (
                        <td className="px-3 py-2 text-right">
                          <ActionMenu
                            title={`Actions for ${d.name || mac || `device #${d.id}`}`}
                            items={[{
                              label: 'Remove from this customer',
                              danger: true,
                              onClick: () => { setConfirmUnassignDev(d); unassignDev.reset(); },
                            }]}
                          />
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      )}


      {/* THE VOICEMAIL SETTINGS THAT ARE GENUINELY THE ACCOUNT'S.
          Whether Verso or the PBX owns the mailboxes, and who gets the emails by default.
          Both are one decision for the customer — flipping the owner per line would have two
          pipelines emailing them, and a default recipient list edited from five line pages is
          five doors to one setting.

          It sits beside Users because that is the roster it draws from. Everything else about
          voicemail — whether THIS line takes messages, which extension holds the mailbox, who
          gets THIS mailbox's mail — is on the line's own Voicemail tab.

          Put here when the "account-wide" drawer was deleted (2026-08-13). The line's tab
          links to it in as many words, so without this that link went to a page that no
          longer had the setting — a worse outcome than the drawer. */}
      {/* Voicemail management MOVED to each line's Voicemail tab, 2026-08-15 (Kevin).
          It sat here because an earlier account-wide drawer was deleted and the
          setting had to live somewhere; the Users tab was never where a voicemail
          question gets asked. See components/lines/LineVoicemail.jsx. */}

      {/* Call History — aggregated across all extensions on this subscriber.
          Backend fans out to the hosting PBX agent(s) and merges by ts desc.
          Empty state when the subscriber has no SIP accounts yet.
          Rendered inline on the Overview tab (no dedicated tab). */}
      {activeTab === 'overview' && (
      <Section
        title="Call History"
        subtitle={(() => {
          if (!hasSipAccounts) return 'No extensions on this subscriber yet.';
          const list = Array.isArray(callsQ.data?.calls) ? callsQ.data.calls : [];
          const winLabel = CALL_WINDOWS.find((w) => w.id === callWindow)?.label || '';
          return list.length === 0
            ? `No calls recorded in the last ${winLabel}.`
            : `${list.length} call${list.length === 1 ? '' : 's'} in the last ${winLabel} across ${callsQ.data?.extension_count ?? 'this'} extension${callsQ.data?.extension_count === 1 ? '' : 's'}.`;
        })()}
        actions={hasSipAccounts ? (
          <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 text-xs">
            {CALL_WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => setCallWindow(w.id)}
                className={`rounded px-2.5 py-1 font-medium transition-colors ${
                  callWindow === w.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
        ) : null}
      >
        {!hasSipAccounts ? null : callsQ.isLoading ? (
          <p className="mt-3 text-sm text-gray-400">Loading call history…</p>
        ) : callsQ.isError ? (
          <p className="mt-3 text-sm text-gray-400">
            Couldn&rsquo;t reach the PBX agent. Data will appear once the agent responds.
          </p>
        ) : Array.isArray(callsQ.data?.calls) && callsQ.data.calls.length > 0 && (
          <div className="mt-3 overflow-visible rounded-md border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Ext</th>
                  <th className="px-3 py-2">Direction</th>
                  <th className="px-3 py-2">From</th>
                  <th className="px-3 py-2">To</th>
                  <th className="px-3 py-2">Duration</th>
                  <th className="px-3 py-2">Result</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {callsQ.data.calls.map((c) => {
                  const ts   = c.ts ?? c.start_ts;
                  const secs = c.billsec ?? c.duration ?? 0;
                  const m = Math.floor(secs / 60);
                  const s = secs % 60;
                  const dur = secs > 0 ? `${m}:${String(s).padStart(2, '0')}` : '—';
                  const kind = c.direction || c.kind;
                  // "ANSWERED" over a call the MAILBOX took is how a dead line reads as a
                  // healthy one — an operator drew exactly that conclusion from this table about a
                  // customer who had no registration for three hours (ticket #19, 2026-08-21).
                  // `answered_by` is only set where the worker could prove it; absent means unknown
                  // and keeps today's wording rather than asserting a person answered.
                  const byVoicemail = c.answered_by === 'voicemail';
                  const disp = String(c.disposition || '').toUpperCase();
                  const dispCls = byVoicemail ? 'bg-amber-100 text-amber-800'
                                : disp === 'ANSWERED' ? 'bg-green-100 text-green-700'
                                  : disp === 'FAILED' ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600';
                  const kindCls = kind === 'inbound'  ? 'bg-green-100 text-green-700'
                                : kind === 'outbound' ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600';
                  return (
                    <tr key={`${c.uniqueid || c.id}-${ts}`} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-3 py-2 text-gray-600">
                        {ts ? new Date(ts * 1000).toLocaleString([], {
                          month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        }) : '—'}
                      </td>
                      {/* WHICH HANDSET ANSWERED, from `dstchannel` — the channel FreePBX recorded,
                          sent by agent v0.39.0. Not `extension`, which only names the query the row
                          arrived on: reading that once produced a confident wrong answer about who
                          was picking up a fire district's calls. Falls back to `extension` on a
                          single-row call, where the two agree. */}
                      <td className="px-3 py-2 font-mono text-xs">
                        {c.answered_extension || (c.merged_rows > 1 ? null : c.extension) || (
                          <span className="text-gray-400">—</span>
                        )}
                        {c.merged_rows > 1 && (
                          <span
                            className="ml-1 font-sans text-[10px] text-gray-400"
                            title={`Merged from ${c.merged_rows} CDR rows — one per extension that took part`}
                          >
                            merged {c.merged_rows}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${kindCls}`}>
                          {kind || 'other'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.src || c.from || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{c.dst || c.to || '—'}</td>
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">{dur}</td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${dispCls}`}
                          title={byVoicemail
                            ? ({
                              lastapp: 'The phone system recorded the voicemail application answering this call',
                              no_channel: 'No phone ever rang — the mailbox took it. Check the line is registered.',
                              message: 'The caller left a message on this mailbox',
                            }[c.answered_by_evidence] || 'The mailbox answered this call')
                            : undefined}
                        >
                          {byVoicemail ? 'voicemail'
                            : c.disposition ? String(c.disposition).toLowerCase() : 'unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      )}

      {/* Modals */}
      {editing && (
        <SubscriberFormModal
          initial={sub}
          title="Edit Subscriber"
          submitLabel="Save"
          portalAllowed={sub.features?.portal_access !== false}
          onSubmit={(body) => patchSub.mutate(body)}
          onCancel={() => { setEditing(false); patchSub.reset(); }}
          isPending={patchSub.isPending}
          error={patchSub.isError ? (patchSub.error?.body?.error || patchSub.error?.message || 'Save failed') : null}
        />
      )}
      <RevealSecretDialog
        account={revealSecret.pending}
        onConfirm={revealSecret.confirm}
        onCancel={revealSecret.cancel}
        isPending={revealSecret.isPending}
        error={revealSecret.error}
      />
      {creatingLoc && (
        <LocationFormModal
          title={`New address for ${sub.name || 'this customer'}`}
          submitLabel="Create and assign"
          // No type pre-set from the customer: the operator chooses, because a customer
          // can legitimately have addresses of both kinds.
          initial={{}}
          onSubmit={(body) => createAndAssignLoc.mutate(body)}
          onCancel={() => { setCreatingLoc(false); createAndAssignLoc.reset(); }}
          isPending={createAndAssignLoc.isPending}
          error={createAndAssignLoc.isError
            ? (createAndAssignLoc.error?.body?.error || createAndAssignLoc.error?.message || 'Could not create that address')
            : null}
        />
      )}
      {confirmUnassignLoc && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Unassign Location"
          body={`Remove "${confirmUnassignLoc.label || `Location #${confirmUnassignLoc.id}`}" from this customer. The address itself is not deleted${locSubCount(confirmUnassignLoc) > 1 ? ', and the other customers there keep it' : ' — it returns to the org pool'}.`}
          confirmLabel="Unassign"
          onConfirm={() => unassignLoc.mutate(confirmUnassignLoc.id)}
          onCancel={() => { setConfirmUnassignLoc(null); unassignLoc.reset(); }}
          isPending={unassignLoc.isPending}
          error={unassignLoc.isError ? (unassignLoc.error?.body?.error || unassignLoc.error?.message || 'Unassign failed') : null}
        />
      )}
      {pendingStatus && (
        <StatusChangeDialog
          subscriberId={id}
          subscriberName={sub.name || `#${id}`}
          from={sub.status}
          to={pendingStatus}
          isPending={changeStatus.isPending}
          error={changeStatus.isError
            ? (changeStatus.error?.body?.error || changeStatus.error?.message || 'Status change failed')
            : null}
          onCancel={() => { setPendingStatus(null); changeStatus.reset(); }}
          onConfirm={(allowedSystems) => changeStatus.mutate({ status: pendingStatus, allowedSystems })}
        />
      )}
      {confirmUnassignDev && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Remove device"
          body={`Return ${confirmUnassignDev.name || confirmUnassignDev.mac || confirmUnassignDev.mac_address || 'this device'} to inventory. Any SIP accounts on it are unbound so it can be given to someone else cleanly.`}
          confirmLabel="Remove"
          onConfirm={() => unassignDev.mutate(confirmUnassignDev.id)}
          onCancel={() => { setConfirmUnassignDev(null); unassignDev.reset(); }}
          isPending={unassignDev.isPending}
          error={unassignDev.isError ? (unassignDev.error?.body?.error || unassignDev.error?.message || 'Could not remove the device') : null}
        />
      )}
      {confirmUnassignDid && (
        <ConfirmDeleteDialog
          destructive={false}
          title="Unassign DID"
          body={`Return ${fmtDid(confirmUnassignDid.e164)} to inventory. This does not release it from Commio.`}
          confirmLabel="Unassign"
          onConfirm={() => unassignDid.mutate(confirmUnassignDid.e164)}
          onCancel={() => { setConfirmUnassignDid(null); unassignDid.reset(); }}
          isPending={unassignDid.isPending}
          error={unassignDid.isError ? (unassignDid.error?.body?.error || unassignDid.error?.message || 'Unassign failed') : null}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          title="Delete Subscriber"
          body={`Soft-delete ${sub.name || `Subscriber #${sub.id}`}. Locations and DIDs return to the org pool; SIP accounts and devices archive alongside. This cannot be undone from the dashboard.`}
          confirmLabel="Delete Subscriber"
          onConfirm={() => deleteSub.mutate()}
          onCancel={() => { setConfirmDelete(false); deleteSub.reset(); }}
          isPending={deleteSub.isPending}
          error={deleteSub.isError ? (deleteSub.error?.body?.error || deleteSub.error?.message || 'Delete failed') : null}
        />
      )}
    </div>
  );
}
